import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  accessProfilesApi,
  modelAssignmentApi,
  providerCatalogApi,
  tasksApi,
  type CatalogModel,
  type CatalogProvider,
} from '../api';
import {
  v3Api,
  type V3Card,
  type V3Collection,
  type V3Home,
  type V3NextAction,
  type V3Resources,
  type V3SystemDiagnostics,
} from './api';

type LoadState<T> = {
  loading: boolean;
  error: string;
  data: T | null;
};

function useLoad<T>(loader: () => Promise<T>, deps: unknown[] = []): LoadState<T> {
  const [state, setState] = useState<LoadState<T>>({ loading: true, error: '', data: null });
  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: '' }));
    loader()
      .then((data) => {
        if (!cancelled) setState({ loading: false, error: '', data });
      })
      .catch((err: unknown) => {
        if (!cancelled) setState({ loading: false, error: err instanceof Error ? err.message : String(err), data: null });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}

function valueText(value: unknown, fallback = '暂无'): string {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function asList(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object') : [];
}

function textList(value: unknown): string[] {
  return Array.isArray(value) ? value.map((item) => valueText(item, '')).filter(Boolean) : [];
}

function fmtTime(value?: string | null): string {
  if (!value) return '暂无时间';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('zh-CN', { hour12: false });
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function percent(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, Math.min(100, Math.round(n))) : 0;
}

function shortText(value: unknown, fallback = '暂无说明', max = 88): string {
  const text = valueText(value, fallback).replace(/\s+/g, ' ').trim();
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

const technicalTextPattern = /\{['"]|route_|provider_greenfield|execution_pipeline|executor_agent|adapter|stage_acceptance_criteria|task_package_acceptance_criteria|model_ref|model_id|backup_route|source_session_id|task_package_id|node_modules/i;
const excludedDeliveryPath = /node_modules|\.cache|cache|tmp|temp|debug|\.log|dist\/assets\/.*\.map/i;

function isTechnicalText(value: unknown): boolean {
  return technicalTextPattern.test(valueText(value, ''));
}

function safeHumanText(value: unknown, fallback = '暂无说明', max = 88): string {
  const text = valueText(value, '');
  if (!text || isTechnicalText(text)) return fallback;
  return shortText(text, fallback, max);
}

function statusText(value: unknown, fallback = '暂无状态'): string {
  const text = valueText(value, fallback);
  if (/key_saved/i.test(text)) return '已保存密钥';
  if (/insufficient_scope/i.test(text)) return '权限不足';
  if (/invalid_config/i.test(text)) return '配置不完整';
  if (/test_failed/i.test(text)) return '连接失败';
  if (/^invalid$/i.test(text)) return '授权失败';
  if (/reachable|ok|healthy/i.test(text)) return '连接正常';
  if (/unreachable|timeout/i.test(text)) return '连接异常';
  if (/accepted|completed|success|succeeded|done/i.test(text)) return '已完成';
  if (/failed|error|rejected|invalid|insufficient_scope/i.test(text)) return '连接失败，需要处理';
  if (/running|processing/i.test(text)) return '正在执行';
  if (/queued|pending|waiting/i.test(text)) return '等待处理';
  return text;
}

function resourceTypeText(value: unknown): string {
  const text = valueText(value, '').toLowerCase();
  if (text === 'vps') return 'VPS 执行资源';
  if (text === 'github') return 'GitHub 代码仓库';
  if (text === 'local') return '本地执行资源';
  return '执行资源';
}

function modelDisplayName(value: unknown): string {
  const text = valueText(value, '尚未记录');
  if (/^manual$|human-review/i.test(text)) return '人工处理';
  if (/codex-cli|^codex$/i.test(text)) return '本地 Codex 执行器';
  return text;
}

function providerDisplayName(value: unknown): string {
  const text = valueText(value, '其他 Provider');
  const labels: Record<string, string> = {
    openai: 'OpenAI',
    minimax: 'MiniMax',
    qwen: 'Qwen',
    deepseek: 'DeepSeek',
    volcengine: '火山方舟',
    codex: '本地 Codex 执行器',
    gemini: 'Gemini',
    manual: '人工验收',
  };
  return labels[text.toLowerCase()] || text;
}

function providerFailureText(value: unknown): string {
  const text = valueText(value, '').trim();
  if (!text) return '暂无';
  if (/auth_failed|authentication|unauthorized|invalid.*key/i.test(text)) return '认证失败，请更新 API Key 后重试。';
  if (/provider server error http 503/i.test(text)) return '供应商服务返回 HTTP 503，请检查 API Base URL 或稍后重试。';
  if (/timeout/i.test(text)) return '连接超时，请检查 API Base URL 和网络。';
  return safeHumanText(text, '连接测试失败，请检查供应商配置。', 120);
}

function runLine(run: Record<string, unknown>): string {
  const status = statusText(run.status, '执行记录');
  const log = safeHumanText(run.latest_log, status === '失败，需要处理' ? '执行失败，原因在诊断详情中。' : '执行记录已更新，详情见诊断。', 120);
  return `${status} · ${log}`;
}

function evidenceText(value: unknown): string {
  const text = valueText(value, '');
  if (!text) return '证据待补充';
  if (/stage_acceptance_criteria/i.test(text)) return '来自阶段验收记录';
  if (/task_package_acceptance_criteria/i.test(text)) return '来自任务验收记录';
  if (isTechnicalText(text)) return '证据已记录，详情见诊断';
  return shortText(text, '证据待补充', 90);
}

function artifactText(value: unknown): string | null {
  const record = asRecord(value);
  const raw = valueText(record.path || record.name || record.title || value, '');
  if (!raw || excludedDeliveryPath.test(raw) || isTechnicalText(raw)) return null;
  return shortText(raw, '产物', 120);
}

function urgencyScore(card: V3Card): number {
  const status = `${card.user_status} ${card.blocking_reason}`.toLowerCase();
  let score = 0;
  if (/失败|卡住|阻塞|error|failed/.test(status)) score += 50;
  if (/等待|待|验收|确认|补充/.test(status)) score += 30;
  if (/正在|执行|运行/.test(status)) score += 20;
  const t = card.updated_at ? new Date(card.updated_at).getTime() : 0;
  return score + Math.floor(t / 1000000000);
}

function ActionButton({ action }: { action: V3NextAction }) {
  if (!action.enabled) {
    return (
      <button className="ghost-button" type="button" disabled title={action.disabled_reason || '当前不可操作'}>
        {action.label}
      </button>
    );
  }
  return <Link className="primary-button v3-action-link" to={action.route}>{action.label}</Link>;
}

function WorkItemRow({
  card,
  selectable = false,
  selected = false,
  onToggle,
}: {
  card: V3Card;
  selectable?: boolean;
  selected?: boolean;
  onToggle?: () => void;
}) {
  return (
    <article className="v3-work-row">
      {selectable ? <input type="checkbox" checked={selected} onChange={onToggle} aria-label={`选择 ${card.title}`} /> : null}
      <div className="v3-work-main">
        <div className="v3-work-title">
          <strong>{card.title}</strong>
          <span className="status-pill">{card.user_status}</span>
        </div>
        <p>{shortText(card.blocking_reason || card.summary, '暂无说明')}</p>
      </div>
      <div className="v3-work-progress">
        <span>{card.progress?.label || '暂无进度'}</span>
        <div className="v3-progress-bar"><i style={{ width: `${percent(card.progress?.percent)}%` }} /></div>
      </div>
      <time>{fmtTime(card.updated_at)}</time>
      <ActionButton action={card.next_action} />
    </article>
  );
}

function ProgressMini({ label, percentValue }: { label: string; percentValue: number }) {
  return (
    <div className="v3-progress-line compact" aria-label={label}>
      <span>{label}</span>
      <div className="v3-progress-bar"><i style={{ width: `${percent(percentValue)}%` }} /></div>
    </div>
  );
}

function Diagnostics({ value }: { value: Record<string, unknown> }) {
  return (
    <details className="v3-diagnostics">
      <summary>查看诊断详情</summary>
      <pre>{JSON.stringify(value, null, 2)}</pre>
    </details>
  );
}

function LoadingState() {
  return <div className="v3-empty">正在读取真实数据…</div>;
}

function ErrorState({ message }: { message: string }) {
  return <div className="error-box">读取失败：{message}</div>;
}

function EmptyState({ text }: { text: string }) {
  return <div className="v3-empty">{text}</div>;
}

function CompactEmpty({ text }: { text: string }) {
  return <p className="v3-compact-empty">{text}</p>;
}

function PageShell({
  eyebrow,
  title,
  description,
  children,
}: {
  eyebrow: string;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="v3-page">
      <div className="v3-page-title">
        <span>{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {children}
    </section>
  );
}

export function V3HomePage() {
  const { loading, error, data } = useLoad<V3Home>(() => v3Api.home(), []);
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <EmptyState text="暂时没有首页数据。" />;

  const overviewCards = data.overview_cards || Object.entries(data.overview || {}).map(([label, value]) => ({
    label,
    value,
    route: label === '正在执行' ? '/v3/progress' : label === '等待验收' ? '/v3/acceptance' : label === '项目交付' ? '/v3/delivery' : '/v3/requirements',
  }));
  const sourceSections = data.sections || {};
  const urgent = (items: V3Card[]) => [...items].sort((a, b) => urgencyScore(b) - urgencyScore(a)).slice(0, 5);
  const sections: Array<[string, V3Card[], string]> = [
    ['今日待办', urgent([...(sourceSections['需要我处理'] || [])]), '今天需要你确认、补充或处理的事项。'],
    ['正在执行', urgent(sourceSections['正在运行'] || []), '系统正在跑、排队或刚产生进展的任务。'],
    ['异常和卡住任务', urgent(sourceSections['异常提醒'] || []), '失败、疑似卡住或需要人工判断的项。'],
    ['最近项目', urgent(sourceSections['最近项目'] || []), '最近更新的正式项目。'],
    ['等待验收', urgent((sourceSections['等待验收'] || sourceSections['需要我处理'] || []).filter((item) => /验收/.test(item.user_status + item.summary + item.next_action.label))), '阶段或项目已经进入验收判断。'],
    ['候选交付', urgent(sourceSections['候选交付'] || []), '可以生成、查看或整理交付物的项目。'],
  ];

  return (
    <PageShell
      eyebrow="V3 信息架构"
      title="工作台"
      description="按紧急程度聚合今天能推进的事情。"
    >
      <div className="v3-kpis clickable">
        {overviewCards.map((item) => (
          <Link className="v3-kpi" to={item.route} key={item.label}>
            <small>{item.label}</small>
            <strong>{item.value}</strong>
          </Link>
        ))}
      </div>
      <div className="v3-entry-row">
        {data.main_actions.map((action) => <ActionButton key={action.label} action={action} />)}
      </div>
      <div className="v3-workbench-grid">
        {sections.map(([name, items, hint]) => (
          <section className="v3-section" key={name}>
            <header className="v3-section-head">
              <h2>{name}</h2>
              <span>{hint}</span>
            </header>
            <div className="v3-work-list">
              {items.length ? items.map((item) => <WorkItemRow key={item.id} card={item} />) : <EmptyState text="暂无记录。" />}
            </div>
          </section>
        ))}
      </div>
    </PageShell>
  );
}

const centerCopy: Record<string, { eyebrow: string; title: string; description: string; empty: string }> = {
  requirements: {
    eyebrow: '需求中心',
    title: '需求中心',
    description: '新需求、接手 GitHub 项目、导入已有大纲、追加需求和后续版本需求会在这里分开显示。',
    empty: '当前没有需要处理的需求。',
  },
  plans: {
    eyebrow: '草稿计划',
    title: '草稿计划',
    description: '这里只放尚未进入正式执行的项目大纲和任务清单。',
    empty: '当前没有草稿计划。',
  },
  projects: {
    eyebrow: '项目执行',
    title: '项目执行',
    description: '这里只放已经生成正式项目和正式任务清单的项目。',
    empty: '当前没有正式执行项目。',
  },
  progress: {
    eyebrow: '实时进度',
    title: '实时进度',
    description: '运行中、等待中、失败和疑似卡住的任务在这里集中查看。',
    empty: '当前没有运行中或失败的任务。',
  },
  acceptance: {
    eyebrow: '验收中心',
    title: '验收中心',
    description: '任务验收、阶段验收、项目验收和回归验收在这里集中查看。',
    empty: '当前没有待验收项目。',
  },
  deliveries: {
    eyebrow: '交付中心',
    title: '交付中心',
    description: '这里只展示真实具备交付条件的项目。问题、返修和 RC 历史第一阶段暂不接入。',
    empty: '当前没有具备交付条件的项目。',
  },
};

function useQueryDefaults() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export function V3CenterPage({ center }: { center: 'requirements' | 'plans' | 'projects' | 'progress' | 'acceptance' | 'deliveries' }) {
  const copy = centerCopy[center];
  const query = useQueryDefaults();
  const [status, setStatus] = useState(query.get('status') || '');
  const [source, setSource] = useState(query.get('source') || '');
  const [updated, setUpdated] = useState(query.get('updated') || '');
  const [view, setView] = useState(query.get('view') || 'active');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [refreshKey, setRefreshKey] = useState(0);
  const [message, setMessage] = useState('');

  const loader = () => {
    if (center === 'requirements') return v3Api.requirements({ status, source, updated, view });
    if (center === 'plans') return v3Api.plans({ status, source, updated, view });
    if (center === 'projects') return v3Api.projects({ status, updated, view });
    if (center === 'progress') return v3Api.progress();
    if (center === 'acceptance') return v3Api.acceptance();
    return v3Api.deliveries();
  };
  const { loading, error, data } = useLoad<V3Collection>(loader, [center, status, source, updated, view, refreshKey]);

  const selectedCards = useMemo(() => {
    const all = data?.items || [];
    return all.filter((card) => selected.has(card.id));
  }, [data, selected]);

  const toggle = (card: V3Card) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(card.id)) next.delete(card.id);
      else next.add(card.id);
      return next;
    });
  };

  const runAction = async (kind: 'delete-session' | 'archive-session' | 'trash-session' | 'restore-session' | 'archive-project' | 'trash-project' | 'restore-project') => {
    if (!selectedCards.length) return;
    const label = {
      'delete-session': '永久删除草稿',
      'archive-session': '归档需求/草稿',
      'trash-session': '移入回收站',
      'restore-session': '恢复需求/草稿',
      'archive-project': '归档项目',
      'trash-project': '移入回收站',
      'restore-project': '恢复项目',
    }[kind];
    if (!window.confirm(`${label}：${selectedCards.length} 条？`)) return;
    setMessage('正在处理…');
    try {
      for (const card of selectedCards) {
        if (kind === 'delete-session' && card.session_id) await v3Api.deleteSession(card.session_id);
        if (kind === 'archive-session' && card.session_id) await v3Api.archiveSession(card.session_id);
        if (kind === 'trash-session' && card.session_id) await v3Api.trashSession(card.session_id);
        if (kind === 'restore-session' && card.session_id) await v3Api.restoreSession(card.session_id);
        if (kind === 'archive-project' && card.project_id) await v3Api.archiveProject(card.project_id);
        if (kind === 'trash-project' && card.project_id) await v3Api.trashProject(card.project_id);
        if (kind === 'restore-project' && card.project_id) await v3Api.restoreProject(card.project_id);
      }
      setSelected(new Set());
      setRefreshKey((x) => x + 1);
      setMessage('已完成，列表和首页统计会在刷新后同步。');
    } catch (err) {
      const text = err instanceof Error ? err.message : String(err);
      const detail = (err as { detail?: Record<string, unknown> }).detail;
      if (
        kind === 'trash-project'
        && (/active_tasks_present/i.test(text) || detail?.code === 'active_tasks_present')
        && window.confirm('项目里还有正在执行或等待验收的任务。是否停止这些阻塞任务，并把项目移入回收站？')
      ) {
        try {
          for (const card of selectedCards) {
            if (card.project_id) await v3Api.trashProject(card.project_id, true);
          }
          setSelected(new Set());
          setRefreshKey((x) => x + 1);
          setMessage('已停止阻塞任务，并将项目移入回收站。');
          return;
        } catch (forceErr) {
          setMessage(forceErr instanceof Error ? forceErr.message : String(forceErr));
          return;
        }
      }
      setMessage(text);
    }
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <EmptyState text={copy.empty} />;

  const selectable = center === 'requirements' || center === 'plans' || center === 'projects';
  const allSelected = data.items.length > 0 && data.items.every((card) => selected.has(card.id));
  return (
    <PageShell eyebrow={copy.eyebrow} title={copy.title} description={copy.description}>
      <div className="v3-toolbar">
        {(center === 'projects' || center === 'requirements' || center === 'plans') ? (
          <select value={view} onChange={(e) => { setView(e.target.value); setSelected(new Set()); }}>
            <option value="active">{center === 'projects' ? '进行中' : '当前'}</option>
            <option value="archived">归档</option>
            <option value="recycle-bin">回收站</option>
            <option value="all">全部</option>
          </select>
        ) : null}
        {(center === 'requirements' || center === 'plans' || center === 'projects') ? (
          <>
            <input value={status} onChange={(e) => setStatus(e.target.value)} placeholder="按状态筛选" />
            {center !== 'projects' ? <input value={source} onChange={(e) => setSource(e.target.value)} placeholder="按来源筛选" /> : null}
            <select value={updated} onChange={(e) => setUpdated(e.target.value)}>
              <option value="">全部时间</option>
              <option value="today">今天更新</option>
              <option value="week">7 天内</option>
              <option value="month">31 天内</option>
            </select>
          </>
        ) : null}
      </div>

      <div className="v3-kpis">
        {Object.entries(data.counts || {}).map(([label, value]) => (
          <div className="v3-kpi" key={label}>
            <small>{label}</small>
            <strong>{value}</strong>
          </div>
        ))}
      </div>

      {selectable ? (
        <div className="v3-bulkbar">
          <label>
            <input
              type="checkbox"
              checked={allSelected}
              onChange={() => setSelected(allSelected ? new Set() : new Set(data.items.map((item) => item.id)))}
            />
            批量选择
          </label>
          <span>已选 {selected.size} 条</span>
          {center === 'projects' ? (
            <>
              {view === 'recycle-bin' ? (
                <button className="ghost-button" onClick={() => void runAction('restore-project')} disabled={!selected.size}>回收站恢复</button>
              ) : (
                <>
                  <button className="ghost-button" onClick={() => void runAction('archive-project')} disabled={!selected.size}>归档</button>
                  <button className="danger-button" onClick={() => void runAction('trash-project')} disabled={!selected.size}>移入回收站</button>
                </>
              )}
              <button className="ghost-button" disabled title="旧项目生命周期目前没有永久删除接口">永久删除</button>
            </>
          ) : (
            <>
              {view === 'archived' || view === 'recycle-bin' ? (
                <button className="ghost-button" onClick={() => void runAction('restore-session')} disabled={!selected.size}>恢复</button>
              ) : (
                <>
                  <button className="ghost-button" onClick={() => void runAction('archive-session')} disabled={!selected.size}>归档</button>
                  <button className="ghost-button" onClick={() => void runAction('trash-session')} disabled={!selected.size}>移入回收站</button>
                </>
              )}
              <button className="danger-button" onClick={() => void runAction('delete-session')} disabled={!selected.size}>永久删除</button>
            </>
          )}
        </div>
      ) : null}

      {message ? <div className="v3-note"><p>{message}</p></div> : null}
      {data.notes?.length ? <div className="v3-note">{data.notes.map((note) => <p key={note}>{note}</p>)}</div> : null}
      <div className="v3-list">
        {data.items.length ? data.items.map((item) => (
          <WorkItemRow
            key={item.id}
            card={item}
            selectable={selectable}
            selected={selected.has(item.id)}
            onToggle={() => toggle(item)}
          />
        )) : <EmptyState text={copy.empty} />}
      </div>
    </PageShell>
  );
}

function DetailGrid({ rows }: { rows: Array<[string, unknown]> }) {
  return (
    <dl className="v3-definition-grid">
      {rows.map(([label, value]) => (
        <div key={label}><dt>{label}</dt><dd>{valueText(value)}</dd></div>
      ))}
    </dl>
  );
}

const NEW_REQUIREMENT_SESSION_KEY = 'v3_new_requirement_session_id';
const IMPORT_OUTLINE_SESSION_KEY = 'v3_import_outline_session_id';

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function V3NewRequirementPage() {
  const [rawRequirement, setRawRequirement] = useState('');
  const [sessionId, setSessionId] = useState(() => window.sessionStorage.getItem(NEW_REQUIREMENT_SESSION_KEY) || '');
  const [draft, setDraft] = useState<Record<string, unknown> | null>(null);
  const [taskDrafts, setTaskDrafts] = useState<Record<string, unknown>[]>([]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const refreshDraft = useCallback(async (id: string) => {
    if (!id) return;
    const next = await v3Api.getRequirementDraft(id);
    setDraft(next);
    setRawRequirement(valueText(next.requirement, ''));
    const formal = asRecord(next.formal_object_state);
    if (formal.created_project_id) {
      window.sessionStorage.removeItem(NEW_REQUIREMENT_SESSION_KEY);
    }
    const qc = asRecord(next.quality_contract);
    const isConfirmed = qc.confirmed === true || next.quality_contract_confirmed === true;
    if (isConfirmed && !formal.created_project_id) {
      const existing = await v3Api.getRequirementTaskDrafts(id).catch(() => null);
      setTaskDrafts(asList(existing && typeof existing === 'object' ? (existing as Record<string, unknown>).drafts : []));
    } else {
      setTaskDrafts([]);
    }
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    refreshDraft(sessionId).catch((err) => setError(errorMessage(err)));
  }, [sessionId, refreshDraft]);

  const runAction = async (label: string, action: () => Promise<void>) => {
    setBusy(label);
    setError('');
    setMessage('');
    try {
      await action();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy('');
    }
  };

  const createDraft = (event: FormEvent) => {
    event.preventDefault();
    runAction('create', async () => {
      if (sessionId) {
        throw new Error(`已有草稿 ${sessionId}，请继续当前流程；如需另建，请先完成或清空当前浏览器会话。`);
      }
      if (!rawRequirement.trim()) throw new Error('请先填写需求内容。');
      const created = await v3Api.createRequirementDraft({ raw_requirement: rawRequirement.trim(), mock_mode: false });
      const id = valueText(created.id || created.session_id, '');
      if (!id) throw new Error('需求已提交，但后端没有返回 session_id。');
      window.sessionStorage.setItem(NEW_REQUIREMENT_SESSION_KEY, id);
      setSessionId(id);
      setMessage('新需求已提交，刷新页面后会继续保留当前草稿。');
      await refreshDraft(id);
    });
  };

  const generateOutline = () => runAction('outline', async () => {
    if (!sessionId) throw new Error('请先提交新需求。');
    const next = await v3Api.generateRequirementOutline(sessionId, { planning_model_id: 'codex-cli' });
    setDraft(next);
    setMessage('项目大纲已生成，quality-contract-v1 已进入等待确认状态。');
  });

  const confirmBaseline = () => runAction('confirm', async () => {
    if (!sessionId) throw new Error('请先提交新需求。');
    const next = await v3Api.confirmRequirementOutline(sessionId);
    setDraft(next);
    setMessage('质量基线已确认，任务拆解入口已放行。');
  });

  const generateTaskDrafts = () => runAction('tasks', async () => {
    if (!sessionId) throw new Error('请先提交新需求。');
    const generated = await v3Api.generateRequirementTaskDrafts(sessionId);
    const generatedDrafts = asList(generated.drafts);
    if (generatedDrafts.length) {
      setTaskDrafts(generatedDrafts);
    } else {
      const data = await v3Api.getRequirementTaskDrafts(sessionId);
      setTaskDrafts(asList(data.drafts));
    }
    setMessage('任务拆解已生成。每个任务只显示与自身相关的质量标准、完成定义和证据要求。');
  });

  const createProject = () => runAction('project', async () => {
    if (!sessionId) throw new Error('请先提交新需求。');
    await v3Api.confirmRequirementTaskDrafts(sessionId);
    const result = await v3Api.createProjectFromRequirement(sessionId);
    await refreshDraft(sessionId);
    window.sessionStorage.removeItem(NEW_REQUIREMENT_SESSION_KEY);
    setMessage(`正式项目已创建：${valueText(result.created_project_id, '项目 ID 待刷新')}`);
  });

  const outline = asRecord(draft?.project_outline);
  const formal = asRecord(draft?.formal_object_state);
  const qualityContract = asRecord(draft?.quality_contract);
  const baseline = asRecord(qualityContract.baseline);
  const confirmed = qualityContract.confirmed === true || draft?.quality_contract_confirmed === true;
  const gate = asRecord(draft?.outline_quality_gate);
  const hasOutline = Boolean(outline.name || draft?.quality_contract);
  const projectId = valueText(formal.created_project_id, '');

  return (
    <PageShell eyebrow="V3 第一阶段" title="创建新需求" description="从这里提交需求、生成项目大纲、确认质量基线，再进入任务拆解和项目创建。">
      <form className="v3-section wide v3-new-requirement-form" onSubmit={createDraft}>
        <header className="v3-section-head">
          <h2>需求内容</h2>
          <span>{sessionId ? `当前草稿 ${sessionId}` : '提交后会创建一份可恢复草稿'}</span>
        </header>
        <textarea
          value={rawRequirement}
          onChange={(event) => setRawRequirement(event.target.value)}
          placeholder="写清产品类型、目标用户、核心流程、数据和交付形式。"
          disabled={Boolean(sessionId)}
        />
        <div className="v3-entry-row">
          <button className="primary-button" type="submit" disabled={Boolean(sessionId) || busy === 'create'}>
            {sessionId ? '已创建草稿' : '提交新需求'}
          </button>
          {sessionId ? <Link className="ghost-button" to={`/v3/requirements/${sessionId}`}>查看需求详情</Link> : null}
          {projectId ? <Link className="primary-button" to={`/v3/projects/${projectId}`}>进入正式项目</Link> : null}
        </div>
      </form>

      {error ? <section className="v3-section wide v3-blocking"><strong>操作失败</strong><p>{error}</p></section> : null}
      {message ? <section className="v3-note"><p>{message}</p></section> : null}

      <section className="v3-section wide">
        <header className="v3-section-head">
          <h2>项目大纲与质量基线</h2>
          <span>{confirmed ? '质量基线已确认' : hasOutline ? '等待用户确认质量基线' : '尚未生成大纲'}</span>
        </header>
        <div className="v3-entry-row">
          <button className="primary-button" onClick={generateOutline} disabled={!sessionId || busy === 'outline' || Boolean(projectId)}>
            生成项目大纲
          </button>
          <button className="ghost-button" onClick={generateTaskDrafts} disabled={!sessionId || busy === 'tasks' || confirmed || Boolean(projectId)}>
            未确认时尝试任务拆解
          </button>
          <button className="primary-button" onClick={confirmBaseline} disabled={!hasOutline || confirmed || busy === 'confirm' || Boolean(projectId)}>
            确认质量基线
          </button>
        </div>
        {hasOutline ? (
          <DetailGrid rows={[
            ['项目名称', outline.name],
            ['目标用户', outline.target_users],
            ['合同版本', qualityContract.contract_version || qualityContract.version],
            ['合同来源', qualityContract.source],
            ['确认状态', confirmed ? `已确认：${valueText(qualityContract.confirmed_at)}` : '等待用户确认'],
            ['阻断原因', confirmed ? '已放行任务拆解' : '质量基线未确认，阶段计划和任务拆解被阻断'],
            ['大纲质量门', gate.passed === false ? textList(gate.reasons).join('；') || '未通过' : '通过或等待检查'],
          ]} />
        ) : <CompactEmpty text="提交需求后先生成项目大纲和 quality-contract-v1。" />}
        {baseline.product_type ? (
          <div className="v3-quality-baseline-mini">
            <strong>质量基线</strong>
            <p>产品类型：{valueText(baseline.product_type)}；交付形式：{textList(baseline.delivery_form).join('、') || '待生成'}</p>
            <p>核心用户旅程：{textList(baseline.core_user_journey).join(' → ') || '待生成'}</p>
          </div>
        ) : null}
      </section>

      <section className="v3-section wide">
        <header className="v3-section-head">
          <h2>任务拆解与项目创建</h2>
          <span>{taskDrafts.length ? `${taskDrafts.length} 个任务草稿` : '等待质量基线确认后生成'}</span>
        </header>
        <div className="v3-entry-row">
          <button className="primary-button" onClick={generateTaskDrafts} disabled={!confirmed || busy === 'tasks' || Boolean(projectId)}>
            生成任务拆解
          </button>
          <button className="primary-button" onClick={createProject} disabled={!taskDrafts.length || busy === 'project' || Boolean(projectId)}>
            确认任务拆解并创建项目
          </button>
        </div>
        {taskDrafts.length ? (
          <div className="v3-quality-task-list">
            {taskDrafts.slice(0, 8).map((task) => (
              <article className="v3-compact-row" key={valueText(task.id || task.title)}>
                <div>
                  <strong>{valueText(task.title, '未命名任务')}</strong>
                  <p>{valueText(task.objective, '暂无目标')}</p>
                </div>
                <span>{valueText(task.status, '草稿')}</span>
              </article>
            ))}
          </div>
        ) : <CompactEmpty text={confirmed ? '质量基线已确认，可以生成任务拆解。' : '未确认质量基线前，任务拆解保持阻断。'} />}
      </section>
    </PageShell>
  );
}

function outlinePayloadFromText(title: string, raw: string): Record<string, unknown> {
  const lines = raw.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const findValue = (labels: string[]) => {
    for (const line of lines) {
      const match = line.match(/^([^：:]{2,18})[：:]\s*(.+)$/);
      if (match && labels.some((label) => match[1].includes(label))) return match[2].trim();
    }
    return '';
  };
  const name = title.trim() || findValue(['项目名称', '名称', '标题']) || lines[0]?.replace(/^#+\s*/, '').slice(0, 40) || '导入的大纲项目';
  const positioning = findValue(['定位', '目标', '简介']) || '基于已导入项目大纲继续规划。';
  const targetUsers = findValue(['目标用户', '用户', '使用者']) || '项目用户、维护人员和验收人员';
  const businessContext = findValue(['背景', '业务', '上下文']) || raw.slice(0, 800);
  const phaseLines = lines.filter((line) => /^(阶段|模块|里程碑|第[一二三四五六七八九十\d]+阶段)/.test(line.replace(/^[-*#\d.、\s]+/, '')));
  const phases = (phaseLines.length ? phaseLines : ['阶段一：大纲整理与核心功能实现']).slice(0, 8).map((line, index) => {
    const cleaned = line.replace(/^[-*#\d.、\s]+/, '').replace(/^阶段[一二三四五六七八九十\d]*[：:\s-]*/, '').trim();
    const phaseName = cleaned.slice(0, 40) || `阶段 ${index + 1}`;
    return {
      phase_number: index + 1,
      name: phaseName,
      task_scope: `${phaseName}：根据导入大纲补齐功能、数据、验收证据和交付物。`,
      estimated_effort: '待评估',
      phase_goal: `${phaseName} 可被用户操作和验收。`,
      module_scope: phaseName,
      deliverables: [`${phaseName} 相关源码、配置和验收记录`],
      risks: ['导入大纲来自人工粘贴，需继续检查质量基线和遗漏项。'],
      criteria: [
        { criterion: `${phaseName} 的核心页面、接口或产物可打开查看。`, priority: 'must' },
        { criterion: `${phaseName} 的验收证据可追溯到原始导入大纲。`, priority: 'must' },
      ],
    };
  });
  return {
    name,
    positioning,
    target_users: targetUsers,
    business_context: businessContext,
    source_outline_text: raw,
    source_requirement_text: raw,
    phases,
    out_of_scope: [],
    risks: ['导入内容需要人工复核，确认质量基线前不得进入任务拆解。'],
    project_acceptance_criteria: [
      '导入后的项目大纲可在草稿详情中查看。',
      '质量合同生成后必须由用户确认。',
      '未确认质量基线前任务拆解被阻断。',
      '确认后任务拆解只继承相关质量标准。',
    ],
  };
}

export function V3ImportOutlinePage() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [rawRequirement, setRawRequirement] = useState('');
  const [outlineText, setOutlineText] = useState('');
  const [sessionId, setSessionId] = useState(() => window.sessionStorage.getItem(IMPORT_OUTLINE_SESSION_KEY) || '');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const importOutline = (event: FormEvent) => {
    event.preventDefault();
    setBusy('import');
    setError('');
    setMessage('');
    (async () => {
      if (sessionId) throw new Error(`已有导入草稿 ${sessionId}，请继续当前流程；如需另建，请先完成或清空当前浏览器会话。`);
      if (!outlineText.trim()) throw new Error('请先粘贴已有项目大纲。');
      const requirement = [
        `[import_outline] ${title.trim() || '导入已有大纲'}`,
        rawRequirement.trim() ? `原始需求：${rawRequirement.trim()}` : '',
        `已有项目大纲：\n${outlineText.trim()}`,
        '这是导入已有大纲，不是从零生成；必须保留用户提供的大纲事实，质量基线确认前不得任务拆解。',
      ].filter(Boolean).join('\n\n');
      const created = await v3Api.createRequirementDraft({ raw_requirement: requirement, mock_mode: false });
      const id = valueText(created.id || created.session_id, '');
      if (!id) throw new Error('草稿已创建，但后端没有返回 session_id。');
      const outline = outlinePayloadFromText(title, outlineText);
      await v3Api.importRequirementOutline(id, outline);
      window.sessionStorage.setItem(IMPORT_OUTLINE_SESSION_KEY, id);
      setSessionId(id);
      setMessage('已有大纲已导入为可恢复草稿，质量合同等待用户确认。');
    })()
      .catch((err) => setError(errorMessage(err)))
      .finally(() => setBusy(''));
  };

  const continueDraft = () => {
    if (sessionId) navigate(`/v3/requirements/${sessionId}`);
  };

  return (
    <PageShell eyebrow="V3 第一阶段" title="导入已有大纲" description="把已有项目大纲导入为可检查草稿，先确认质量基线，再进入任务拆解。">
      <form className="v3-section wide v3-new-requirement-form" onSubmit={importOutline}>
        <header className="v3-section-head">
          <h2>已有大纲</h2>
          <span>{sessionId ? `当前导入草稿 ${sessionId}` : '粘贴后创建可恢复草稿'}</span>
        </header>
        <label>
          <span>项目名称</span>
          <input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="例如：单页待办清单网页" disabled={Boolean(sessionId)} />
        </label>
        <label>
          <span>原始需求或背景</span>
          <textarea value={rawRequirement} onChange={(event) => setRawRequirement(event.target.value)} placeholder="可选：补充这个大纲来自什么需求。" disabled={Boolean(sessionId)} />
        </label>
        <label>
          <span>项目大纲正文</span>
          <textarea value={outlineText} onChange={(event) => setOutlineText(event.target.value)} placeholder="粘贴已有大纲，包含阶段、模块、验收标准更好。" disabled={Boolean(sessionId)} />
        </label>
        <div className="v3-entry-row">
          <button className="primary-button" type="submit" disabled={Boolean(sessionId) || busy === 'import'}>
            {sessionId ? '已创建导入草稿' : '导入并创建草稿'}
          </button>
          {sessionId ? <button className="primary-button" type="button" onClick={continueDraft}>继续查看草稿</button> : null}
          {sessionId ? <Link className="ghost-button" to={`/v3/plans/${sessionId}/outline`}>查看大纲</Link> : null}
        </div>
      </form>
      {error ? <section className="v3-section wide v3-blocking"><strong>导入失败</strong><p>{error}</p></section> : null}
      {message ? <section className="v3-note"><p>{message}</p></section> : null}
    </PageShell>
  );
}

function SessionDetailBody({ data, mode }: { data: Record<string, unknown>; mode: 'requirement' | 'plan' }) {
  const blueprint = data.blueprint && typeof data.blueprint === 'object' ? data.blueprint as Record<string, unknown> : null;
  const taskDrafts = asList(data.task_drafts);
  const links = (data.links || {}) as Record<string, string>;
  const clarifyStatus = valueText(data.clarify_status, '');
  const taskPlanStatus = valueText(data.task_plan_status, '');
  const outlineReady = clarifyStatus === 'sufficient' || Boolean(blueprint);
  const taskPlanReady = taskDrafts.length > 0 || taskPlanStatus.includes('已生成');
  const primaryHref = links.project || (taskPlanReady ? links.tasks : outlineReady ? links.outline : links.clarify);
  const primaryLabel = links.project
    ? '查看正式项目'
    : taskPlanReady
      ? '检查任务清单'
      : outlineReady
        ? '检查并确认项目大纲'
        : '继续需求确认';
  return (
    <>
      <DetailGrid rows={[
        ['来源', data.source],
        ['生命周期', data.lifecycle_view],
        ['当前状态', data.status],
        ['澄清状态', data.clarify_status],
        ['任务清单状态', data.task_plan_status],
        ['更新时间', fmtTime(data.updated_at as string | null)],
      ]} />
      <div className="v3-entry-row">
        {primaryHref ? <Link className="primary-button" to={primaryHref}>{primaryLabel}</Link> : null}
        {links.clarify ? <Link className="ghost-button" to={links.clarify}>继续需求确认</Link> : null}
        {links.outline ? <Link className="ghost-button" to={links.outline}>检查项目大纲</Link> : null}
        {links.tasks ? <Link className="ghost-button" to={links.tasks}>生成或检查任务清单</Link> : null}
      </div>
      <section className="v3-section wide">
        <h2>{mode === 'requirement' ? '需求原文' : '草稿概览'}</h2>
        <p>{valueText(data.raw_requirement, '暂无需求原文')}</p>
        {data.current_question ? <p><strong>当前问题：</strong>{valueText(data.current_question)}</p> : null}
        {data.recommended_answer ? <p><strong>建议回答：</strong>{valueText(data.recommended_answer)}</p> : null}
        {data.last_error ? <p className="v3-blocking">原因：{valueText(data.last_error)}</p> : null}
      </section>
      {blueprint ? (
        <section className="v3-section wide">
          <h2>项目大纲</h2>
          <DetailGrid rows={[
            ['大纲名称', blueprint.name],
            ['大纲状态', blueprint.status],
            ['版本', blueprint.version],
            ['目标用户', blueprint.target_users],
            ['定位', blueprint.positioning],
            ['项目目标', blueprint.project_goal],
            ['项目范围', blueprint.project_scope],
          ]} />
        </section>
      ) : null}
      <section className="v3-section wide">
        <h2>任务草稿</h2>
        {taskDrafts.length ? taskDrafts.map((task) => (
          <article className="v3-subcard" key={valueText(task.task_draft_id)}>
            <h3>{valueText(task.title)}</h3>
            <p>{valueText(task.objective)}</p>
            <DetailGrid rows={[
              ['状态', task.status],
              ['预期交付物', task.expected_deliverable],
              ['执行路由', task.executor_agent],
              ['推荐模型', task.recommended_code_model],
            ]} />
            {Array.isArray(task.task_acceptance) && task.task_acceptance.length ? (
              <ul>{task.task_acceptance.map((item) => <li key={String(item)}>{String(item)}</li>)}</ul>
            ) : null}
          </article>
        )) : <EmptyState text="暂无任务草稿。" />}
      </section>
      <Diagnostics value={(data.diagnostics || {}) as Record<string, unknown>} />
    </>
  );
}

export function V3RequirementDetailPage() {
  const { requirementId = '' } = useParams();
  const { loading, error, data } = useLoad<Record<string, unknown>>(() => v3Api.requirementDetail(requirementId), [requirementId]);
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <EmptyState text="没有找到需求。" />;
  return (
    <PageShell eyebrow="需求中心" title={valueText(data.title, '需求详情')} description="这里展示现有需求 session 的真实状态、问题和草稿关联。">
      <SessionDetailBody data={data} mode="requirement" />
    </PageShell>
  );
}

export function V3PlanDetailPage({ section }: { section: 'overview' | 'outline' | 'tasks' | 'history' }) {
  const { sessionId = '' } = useParams();
  const { loading, error, data } = useLoad<Record<string, unknown>>(() => v3Api.planDetail(sessionId, section), [sessionId, section]);
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <EmptyState text="没有找到草稿计划。" />;
  const sectionTitle = { overview: '草稿概览', outline: '项目大纲', tasks: '任务清单', history: '计划历史' }[section];
  return (
    <PageShell eyebrow="草稿计划" title={`${valueText(data.title, '草稿计划')} · ${sectionTitle}`} description="这里展示草稿计划现有真实数据；需要编辑、确认或生成任务时，进入对应操作页完成。">
      <SessionDetailBody data={data} mode="plan" />
    </PageShell>
  );
}

export function V3ProjectDetailPage() {
  const { projectId = '' } = useParams();
  const { loading, error, data } = useLoad<Record<string, unknown>>(() => v3Api.projectDetail(projectId), [projectId]);
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <EmptyState text="没有找到项目。" />;
  const stages = asList(data.stages);
  const recentRuns = asList(data.recent_runs);
  const files = asList(data.generated_files).filter((file) => {
    const path = valueText(file.path, '');
    return path && !excludedDeliveryPath.test(path) && !isTechnicalText(path);
  });
  const links = (data.links || {}) as Record<string, string>;
  const progress = asRecord(data.progress);
  const overall = asRecord(progress.overall);
  const stageProgress = asRecord(progress.stages);
  const currentStage = asRecord(data.current_stage);
  const currentTask = asRecord(data.current_task);
  const projectView = asRecord(data.project_user_view);
  const terminalProject = valueText(projectView.project_status, valueText(data.raw_status)).toLowerCase() === 'accepted_delivered';
  const projectStatus = valueText(projectView.project_status, valueText(data.raw_status)).toLowerCase();
  const currentProjectStatus = valueText(projectView.user_visible_project_status, valueText(data.status, '状态暂时无法识别'));
  const overallPercent = percent(overall.percent);
  const completedTasks = Number(overall.done || 0);
  const totalTasks = Number(overall.total || 0);
  const allTasksDone = totalTasks > 0 && completedTasks >= totalTasks;
  const currentStageName = terminalProject ? '全部阶段已完成' : currentStage.name || '等待生成阶段';
  const currentTaskName = terminalProject ? '无当前任务' : currentTask.title || '等待任务进入执行';
  const nextAction = terminalProject
    ? { label: '查看交付物', route: links.delivery || `/v3/delivery/${projectId}` }
    : projectStatus === 'awaiting_project_acceptance'
      ? { label: '去项目验收', route: links.acceptance || `/v3/acceptance/projects/${projectId}` }
    : projectStatus === 'project_repair_required'
      ? { label: '查看返修', route: `/v3/projects/${projectId}/repairs` }
    : links.progress
      ? { label: '查看实时进度', route: links.progress }
      : { label: '返回项目列表', route: '/v3/projects' };
  const blockers = [
    ...(terminalProject ? [] : asList(data.failed_tasks)).map((task) => ({
      title: valueText(task.title, '失败任务'),
      reason: valueText(task.error || task.status, '执行失败，等待处理'),
    })),
  ];
  return (
    <PageShell eyebrow="项目执行" title={valueText(data.title, '项目执行详情')} description="项目当前状态、完成度、阻塞项和下一步。">
      <section className="v3-product-hero">
        <div>
          <span className="v3-eyebrow">{valueText(data.product_type, '产品')}</span>
          <h2>{valueText(data.title, '项目')}</h2>
          <p>{terminalProject ? '项目已完成，交付物可下载。' : shortText(data.summary, '系统正在整理项目当前状态。', 140)}</p>
        </div>
        <div className="v3-percent-ring"><strong>{allTasksDone ? 100 : overallPercent}%</strong><span>总体完成</span></div>
        <dl>
          <div><dt>任务</dt><dd>{completedTasks}/{totalTasks}</dd></div>
          <div><dt>阶段</dt><dd>{valueText(stageProgress.done, '0')}/{valueText(stageProgress.total, '0')}</dd></div>
          <div><dt>状态</dt><dd>{currentProjectStatus}</dd></div>
          <div><dt>更新时间</dt><dd>{fmtTime(data.updated_at as string | null)}</dd></div>
        </dl>
        <div className="v3-next-step">
          <span>当前阶段</span>
          <strong>{valueText(currentStageName)}</strong>
          <span>当前任务</span>
          <strong>{valueText(currentTaskName)}</strong>
          {data.blocking_reason && !terminalProject ? <p>{valueText(data.blocking_reason)}</p> : <p>{terminalProject ? '当前没有需要用户处理的动作。' : '暂无明确阻塞，继续观察执行进度。'}</p>}
          <Link className="primary-button" to={nextAction.route}>{nextAction.label}</Link>
        </div>
      </section>

      <section className="v3-section wide">
        <header className="v3-section-head"><h2>阶段时间线</h2><span>每个阶段的任务数、完成度和阻塞项。</span></header>
        <div className="v3-stage-timeline">
          {stages.map((stage, idx) => {
            const tasks = asList(stage.tasks);
            const progress = asRecord(stage.progress);
            const done = Number(stage.completed_tasks ?? progress.done ?? tasks.filter((task) => /已通过|完成|accepted/i.test(valueText(task.status))).length);
            const total = Number(stage.total_tasks ?? progress.total ?? tasks.length);
            const failed = Boolean(stage.has_failure) || tasks.some((task) => /失败|拒绝|failed|rejected/i.test(valueText(task.status)));
            const pct = percent(progress.percent ?? (total ? Math.round((done / total) * 100) : 0));
            const open = Boolean(stage.default_expanded) || failed;
            return (
              <details className="v3-stage-node compact" key={valueText(stage.stage_id, String(idx))} open={open}>
                <summary>
                  <span>{idx + 1}</span>
                  <strong>{valueText(stage.name, `第 ${idx + 1} 阶段`)}</strong>
                  <em>{done}/{total} 任务</em>
                  <em>{pct}%</em>
                  <i>{valueText(stage.status)}</i>
                </summary>
                <div className="v3-stage-detail">
                  {failed ? <p className="v3-blocking">存在失败或未通过任务，请优先处理。</p> : null}
                  {tasks.length ? tasks.slice(0, 6).map((task) => (
                    <p key={valueText(task.task_package_id || task.title)}><strong>{shortText(task.title, '任务', 72)}</strong><span>{valueText(task.status)}</span></p>
                  )) : <EmptyState text="本阶段暂无任务。" />}
                </div>
              </details>
            );
          })}
        </div>
      </section>

      <div className="v3-detail-columns">
        <section className="v3-section">
          <h2>失败和阻塞项</h2>
          {blockers.length ? blockers.map((item) => <p key={item.title}><strong>{item.title}</strong>：{item.reason}</p>) : <EmptyState text={terminalProject ? '没有当前阻塞项或用户动作。' : '暂无失败或阻塞记录。'} />}
        </section>
        <section className="v3-section">
          <h2>最近执行记录</h2>
          {recentRuns.length ? recentRuns.map((run) => <p key={valueText(run.run_id)}>{runLine(run)}</p>) : <EmptyState text="暂无执行记录。" />}
        </section>
      </div>

      <section className="v3-section wide">
        <header className="v3-section-head"><h2>生成文件</h2><span>来自真实执行报告；完整路径在诊断里查看。</span></header>
        {files.length ? (
          <div className="v3-file-grid">{files.slice(0, 12).map((file) => <span key={valueText(file.path)}>{shortText(file.path, '文件', 54)}</span>)}</div>
        ) : <EmptyState text="暂无生成文件记录。" />}
      </section>

      <div className="v3-entry-row">
        {links.progress ? <Link className="primary-button" to={links.progress}>去实时进度</Link> : null}
        {links.acceptance ? <Link className="ghost-button" to={links.acceptance}>去验收</Link> : null}
        {links.delivery ? <Link className="ghost-button" to={links.delivery}>去交付</Link> : null}
        <Link className="ghost-button" to={`/v3/projects/${projectId}/quality`}>质量审计</Link>
        <Link className="ghost-button" to={`/v3/projects/${projectId}/issues`}>问题中心</Link>
      </div>
      <Diagnostics value={(data.diagnostics || {}) as Record<string, unknown>} />
    </PageShell>
  );
}

export function V3ProjectQualityPage() {
  const { projectId = '' } = useParams();
  const { loading, error, data } = useLoad<Record<string, unknown>>(() => v3Api.projectQualityContract(projectId), [projectId]);
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <EmptyState text="没有找到质量审计数据。" />;
  const contract = asRecord(data.quality_contract);
  const baseline = asRecord(contract.baseline);
  const report = asRecord(data.quality_report);
  const projectView = asRecord(data.project_user_view);
  const currentProjectStatus = valueText(projectView.user_visible_project_status, valueText(data.status, '状态暂时无法识别'));
  const provisional = asRecord(report.provisional_score);
  const verified = asRecord(report.verified_score);
  const evidenceSummary = asRecord(report.evidence_summary);
  const gates = asRecord(report.delivery_gates);
  const dimensions = asList(report.dimensions || report.category_scores);
  const redLines = asList(report.red_lines);
  const failedItems = textList(report.failed_items);
  const blockingReasons = textList(report.blocking_reasons);
  const qualityIssues = asList(report.quality_issues);
  const baselineRows: Array<[string, unknown]> = [
    ['产品类型', baseline.product_type],
    ['目标用户', baseline.target_users],
    ['核心用户旅程', textList(baseline.core_user_journey).join(' → ')],
    ['功能真实性要求', textList(baseline.functional_authenticity_requirements).join('；')],
    ['数据要求', textList(baseline.data_requirements).join('；')],
    ['视觉要求', textList(baseline.visual_requirements).join('；')],
    ['稳定性要求', textList(baseline.stability_requirements).join('；')],
    ['兼容环境', textList(baseline.compatible_environments).join('；')],
    ['交付形式', textList(baseline.delivery_form).join('；')],
    ['正式交付门槛', textList(baseline.formal_delivery_gate).join('；')],
    ['不可接受项', textList(baseline.unacceptable_items).join('；')],
  ];
  const scoreLabel = valueText(provisional.label || provisional.score, '暂无');
  const verifiedLabel = valueText(verified.label, '未完成');
  return (
    <PageShell eyebrow="质量审计" title={valueText(data.title, '项目质量审计')} description="把质量要求从大纲、任务、阶段、项目验收到候选交付串起来，并说明每一分从哪里来。">
      <section className="v3-product-hero compact">
        <div>
          <span className="v3-eyebrow">{valueText(data.product_type, '产品')}</span>
          <h2>{valueText(data.title, '项目')}</h2>
          <p>{valueText(provisional.note, '暂估，不代表正式验收')}</p>
        </div>
        <dl>
          <div><dt>暂估质量</dt><dd>{scoreLabel}</dd></div>
          <div><dt>正式验证</dt><dd>{valueText(verified.approved_items, '0')}/{valueText(verified.total_items, '0')} 项</dd></div>
          <div><dt>正式质量分</dt><dd>{verifiedLabel}</dd></div>
          <div><dt>当前项目状态</dt><dd>{currentProjectStatus}</dd></div>
          <div><dt>质量门</dt><dd>{valueText(report.passed) === 'true' ? '通过' : '阻断'}</dd></div>
        </dl>
        <div className="v3-next-step">
          <span>交付门槛依据</span>
          <strong>{valueText(gates.basis, '只使用正式验证、审核项和质量红线。')}</strong>
          <Link className="primary-button" to={`/v3/projects/${projectId}/issues`}>查看质量问题</Link>
        </div>
      </section>

      <section className="v3-section wide">
        <header className="v3-section-head"><h2>质量合同版本</h2><span>{valueText(contract.retroactive_notice, '大纲确认时冻结，执行后修改必须生成新版本。')}</span></header>
        <DetailGrid rows={[
          ['contract_version', contract.contract_version || contract.version],
          ['创建时间', fmtTime(contract.created_at as string | null)],
          ['确认时间', fmtTime(contract.confirmed_at as string | null)],
          ['确认人', contract.confirmed_by],
          ['来源', contract.source],
          ['立项时确认', contract.source === 'retroactive_backfill' ? '否' : (valueText(contract.confirmed) === 'true' ? '是' : '否')],
          ['补录时间', contract.source === 'retroactive_backfill' ? fmtTime(contract.created_at as string | null) : '不适用'],
          ['revision', contract.revision],
          ['确认状态', valueText(contract.confirmed) === 'true' ? '已确认' : '未确认'],
          ['变更历史', textList(contract.change_history).join('；') || '暂无变更记录'],
        ]} />
      </section>

      <section className="v3-section wide">
        <header className="v3-section-head"><h2>质量基线</h2><span>这些要求从项目大纲贯穿到任务、阶段、项目和交付验收。</span></header>
        <DetailGrid rows={baselineRows} />
      </section>

      <section className="v3-section wide">
        <header className="v3-section-head"><h2>质量红线</h2><span>任一红线触发都阻断交付。</span></header>
        {redLines.length ? redLines.map((item) => (
          <article className="v3-quality-alert" key={valueText(item.name)}>
            <strong>{valueText(item.name)}</strong>
            <p>{valueText(item.reason)}</p>
          </article>
        )) : <CompactEmpty text="当前未触发质量红线。" />}
      </section>

      <section className="v3-section wide">
        <header className="v3-section-head"><h2>八个维度分数</h2><span>每个维度可展开查看检查项、证据和扣分原因。</span></header>
        <div className="v3-quality-dimension-grid">
          {dimensions.map((dimension) => {
            const items = asList(dimension.items);
            return (
              <details className="v3-quality-dimension" key={valueText(dimension.name || dimension.dimension_id)}>
                <summary>
                  <strong>{valueText(dimension.name || dimension.dimension_id)}</strong>
                  <span>{valueText(dimension.score)} 分</span>
                  <em>{valueText(dimension.status)}</em>
                </summary>
                <p>{valueText(dimension.expected_requirement)}</p>
                {items.map((item) => (
                  <article className="v3-quality-check" key={valueText(item.criterion_id)}>
                    <header>
                      <strong>{valueText(item.criterion_id)} · {valueText(item.check_name)}</strong>
                      <span>{valueText(item.status)} · {valueText(item.score)} 分</span>
                    </header>
                    <DetailGrid rows={[
                      ['权重', item.weight],
                      ['预期要求', item.expected_requirement],
                      ['实际结果', item.actual_result],
                      ['证据引用', textList(item.evidence_refs).join('；') || '暂无正式证据'],
                      ['证据类型', item.evidence_type],
                      ['证据审核状态', item.evidence_review_status],
                      ['扣分原因', item.deduction_reason],
                      ['判断来源', item.judgment_source],
                      ['关联任务', textList(item.related_tasks).join('；') || '暂无'],
                      ['关联问题', textList(item.related_issues).join('；') || '暂无'],
                      ['建议修复', item.suggested_fix],
                    ]} />
                  </article>
                ))}
              </details>
            );
          })}
        </div>
      </section>

      <div className="v3-detail-columns">
        <section className="v3-section">
          <h2>正式验证状态</h2>
          <DetailGrid rows={[
            ['正式分来源', verified.source],
            ['正式分说明', verified.note],
            ['已审核通过', `${valueText(evidenceSummary.approved_task_count, '0')}/${valueText(evidenceSummary.repair_task_count, '0')}`],
            ['证据不足', evidenceSummary.evidence_insufficient_count],
            ['修改文件数', evidenceSummary.modified_file_count],
            ['RC2 SHA-256', evidenceSummary.rc2_sha256],
          ]} />
        </section>
        <section className="v3-section">
          <h2>交付门槛</h2>
          <DetailGrid rows={[
            ['允许生成候选版本', valueText(gates.allow_candidate_version) === 'true' ? '是' : '否'],
            ['允许进入用户试用', valueText(gates.allow_user_trial) === 'true' ? '是' : '否'],
            ['允许正式交付', valueText(gates.allow_formal_delivery) === 'true' ? '是' : '否'],
          ]} />
        </section>
      </div>

      <section className="v3-section wide">
        <header className="v3-section-head"><h2>阻断问题与修复建议</h2><span>质量问题也会进入统一问题中心。</span></header>
        {failedItems.concat(blockingReasons).slice(0, 12).map((item, idx) => <p key={`${idx}-${item}`}>{item}</p>)}
        {qualityIssues.length ? qualityIssues.map((issue) => (
          <article className="v3-issue-row" key={valueText(issue.issue_id)}>
            <header className="v3-issue-head">
              <div>
                <strong>{valueText(issue.title)}</strong>
                <span>{valueText(issue.step)}</span>
              </div>
              <em>{valueText(issue.status)}</em>
            </header>
            <p>{valueText(issue.next_action)}</p>
          </article>
        )) : <CompactEmpty text="暂无质量问题。" />}
      </section>
    </PageShell>
  );
}

export function V3ProjectIssuesPage() {
  const { projectId = '' } = useParams();
  const [refreshTick, setRefreshTick] = useState(0);
  const [evidenceTitle, setEvidenceTitle] = useState('');
  const [evidenceDescription, setEvidenceDescription] = useState('');
  const [evidenceSteps, setEvidenceSteps] = useState('');
  const [evidenceScreenshots, setEvidenceScreenshots] = useState('');
  const [evidenceLogs, setEvidenceLogs] = useState('');
  const [evidenceArtifacts, setEvidenceArtifacts] = useState('');
  const [evidenceAcceptanceItems, setEvidenceAcceptanceItems] = useState('');
  const [evidenceRelatedIssues, setEvidenceRelatedIssues] = useState('');
  const [evidenceRelatedTask, setEvidenceRelatedTask] = useState('');
  const [reviewReason, setReviewReason] = useState('');
  const [evidenceMessage, setEvidenceMessage] = useState('');
  const [busyEvidence, setBusyEvidence] = useState('');
  const { loading, error, data } = useLoad<Record<string, unknown>>(() => v3Api.projectIssues(projectId), [projectId, refreshTick]);
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <EmptyState text="没有找到问题中心数据。" />;
  const coverage = asList(data.coverage);
  const issues = asList(data.issues);
  const audits = asList(data.repair_task_audits);
  const manualEvidence = asList(data.manual_evidence);
  const formalManualEvidence = manualEvidence.filter((item) => valueText(item.evidence_scope, 'formal') !== 'test_only' && valueText(item.evidence_kind, 'manual') !== 'production_smoke' && valueText(item.excluded_from_rc2_acceptance) !== 'true');
  const systemTestEvidence = manualEvidence.filter((item) => !formalManualEvidence.includes(item));
  const unresolved = asList(data.unresolved);
  const rcDiff = asRecord(data.rc_diff);
  const projectView = asRecord(data.project_user_view);
  const terminalProject = valueText(projectView.project_status).toLowerCase() === 'accepted_delivered';
  const currentProjectStatus = valueText(projectView.user_visible_project_status, valueText(data.status, '状态暂时无法识别'));
  const listText = (value: unknown, fallback = '暂无') => {
    const items = textList(value);
    return items.length ? items.join('；') : valueText(value, fallback);
  };
  const splitLines = (value: string) => value.split(/\n|；|;/).map((item) => item.trim()).filter(Boolean);
  const submitEvidence = async (event: FormEvent) => {
    event.preventDefault();
    setBusyEvidence('submit');
    setEvidenceMessage('');
    try {
      await v3Api.submitManualEvidence(projectId, {
        version: 'RC2',
        title: evidenceTitle,
        description: evidenceDescription,
        operation_steps: splitLines(evidenceSteps),
        screenshots: splitLines(evidenceScreenshots),
        logs: splitLines(evidenceLogs),
        artifact_paths: splitLines(evidenceArtifacts),
        acceptance_items: splitLines(evidenceAcceptanceItems),
        related_issue_ids: splitLines(evidenceRelatedIssues),
        related_repair_task_ids: splitLines(evidenceRelatedTask),
        result: 'pending_review',
        tester: 'user',
      });
      setEvidenceTitle('');
      setEvidenceDescription('');
      setEvidenceSteps('');
      setEvidenceScreenshots('');
      setEvidenceLogs('');
      setEvidenceArtifacts('');
      setEvidenceAcceptanceItems('');
      setEvidenceRelatedIssues('');
      setEvidenceRelatedTask('');
      setEvidenceMessage('人工验收证据已提交，等待审核。');
      setRefreshTick((value) => value + 1);
    } catch (err) {
      setEvidenceMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyEvidence('');
    }
  };
  const reviewEvidence = async (evidenceId: string, decision: 'approve' | 'reject') => {
    setBusyEvidence(evidenceId);
    setEvidenceMessage('');
    try {
      await v3Api.reviewManualEvidence(projectId, evidenceId, {
        decision,
        reason: reviewReason || (decision === 'approve' ? '人工验收材料可作为 RC2 候选证据。' : '证据不足，需要补充截图、日志或操作记录。'),
        opinion: reviewReason,
        reviewer: 'user',
      });
      setEvidenceMessage(decision === 'approve' ? '人工证据已审核通过。' : '人工证据已退回补充。');
      setReviewReason('');
      setRefreshTick((value) => value + 1);
    } catch (err) {
      setEvidenceMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyEvidence('');
    }
  };
  return (
    <PageShell eyebrow="统一问题中心" title={valueText(data.title, '项目问题中心')} description={terminalProject ? '查看当前问题；历史返修和验收记录仅在审计区保留。' : '查看当前问题、返修任务和验收证据。'}>
      <section className="v3-product-hero compact">
        <div>
          <span className="v3-eyebrow">{valueText(data.product_type, '产品')}</span>
          <h2>{valueText(data.title, '项目')}</h2>
          <p>{valueText(data.source, '聚合现有真实问题来源，不新建脱离旧数据的假问题表。')}</p>
        </div>
        <dl>
          <div><dt>问题总数</dt><dd>{issues.length}</dd></div>
          <div><dt>未解决/证据不足</dt><dd>{unresolved.length}</dd></div>
          <div><dt>返修任务</dt><dd>{audits.length}</dd></div>
          <div><dt>当前状态</dt><dd>{currentProjectStatus}</dd></div>
        </dl>
        <div className="v3-next-step">
          <span>下一步</span>
          <strong>{terminalProject && issues.length === 0 ? '当前没有需要处理的问题' : unresolved.length ? '先补齐阻断证据，再验收候选交付' : '等待用户确认下一步'}</strong>
          <Link className="ghost-button" to={`/v3/projects/${projectId}/quality`}>质量审计</Link>
          {!terminalProject ? <Link className="primary-button" to={`/v3/projects/${projectId}/repairs`}>进入返修执行</Link> : null}
          <Link className="ghost-button" to={`/v3/delivery/${projectId}`}>返回交付中心</Link>
        </div>
      </section>

      <section className="v3-section wide">
        <header className="v3-section-head"><h2>问题覆盖范围</h2><span>没有真实来源的类型会明确显示。</span></header>
        <div className="v3-file-category-grid">
          {coverage.map((item) => (
            <article key={valueText(item.source)}>
              <strong>{valueText(item.source)}</strong>
              <span>{valueText(item.available) === 'true' ? '已有来源' : '暂无独立来源'} · {valueText(item.count, '0')} 条</span>
              <em>{valueText(item.note)}</em>
            </article>
          ))}
        </div>
      </section>

      <section className="v3-section wide">
        <header className="v3-section-head"><h2>{terminalProject ? '历史版本与返修记录' : '候选版本与返修记录'}</h2><span>{terminalProject ? '历史记录仅供审计，不代表当前项目状态。' : '候选版本与返修证据仅用于当前验收。'}</span></header>
        <DetailGrid rows={[
          ['RC1 revision', rcDiff.rc1_revision],
          ['RC1 SHA-256', rcDiff.rc1_sha256],
          ['RC2 revision', rcDiff.rc2_revision],
          ['RC2 SHA-256', rcDiff.rc2_sha256],
          ['新增文件', listText(rcDiff.added_files)],
          ['修改文件', listText(rcDiff.modified_files)],
          ['新增功能', listText(rcDiff.added_features)],
          ['修复问题', listText(rcDiff.fixed_issues)],
          ['未解决问题', listText(rcDiff.unresolved_issues)],
          ['回归测试结果', rcDiff.regression_result],
        ]} />
      </section>

      <section className="v3-section wide">
        <header className="v3-section-head"><h2>问题列表</h2><span>按出现步骤展示实际结果、期望结果和证据。</span></header>
        {issues.length ? issues.map((issue) => (
          <article className="v3-issue-row" key={valueText(issue.issue_id)}>
            <header className="v3-issue-head">
              <div>
                <strong>{valueText(issue.title, '问题')}</strong>
                <span>{valueText(issue.step)}</span>
              </div>
              <em>{valueText(issue.status)}</em>
            </header>
            <DetailGrid rows={[
              ['当前内容', issue.current_content],
              ['实际结果', issue.actual_result],
              ['期望结果', issue.expected_result],
              ['为什么有问题', issue.why],
              ['客观证据', listText(issue.objective_evidence)],
              ['严重程度', issue.severity],
              ['是否阻断', valueText(issue.blocking) === 'true' ? '是' : '否'],
              ['影响范围', issue.impact],
              ['对应任务', issue.related_task],
              ['修复版本', issue.fix_version],
              ['回归验收结果', issue.regression_result],
              ['下一步操作', issue.next_action],
            ]} />
          </article>
        )) : <EmptyState text="暂无问题记录。" />}
      </section>

      <section className="v3-section wide">
        <header className="v3-section-head"><h2>{terminalProject ? '历史返修任务审计' : '返修任务真实执行审计'}</h2><span>不按 completed 状态判定，只看 run、文件、产物、测试和证据。</span></header>
        {audits.length ? (
          <div className="v3-audit-table">
            <div className="v3-audit-head">
              <span>task_id</span><span>RC1 问题</span><span>run / 模型</span><span>时间</span><span>文件与产物</span><span>测试与证据</span><span>验收</span>
            </div>
            {audits.map((row) => (
              <article className="v3-audit-row" key={valueText(row.task_id)}>
                <span>{valueText(row.task_id)}</span>
                <span>{valueText(row.related_issue_id, '未关联')}</span>
                <span>{valueText(row.execution_run_id, '无 execution run')}<br />{modelDisplayName(row.actual_model)}</span>
                <span>{fmtTime(row.started_at as string | null)}<br />{fmtTime(row.ended_at as string | null)}</span>
                <span>文件：{listText(row.changed_files)}<br />产物：{listText(row.artifacts)}</span>
                <span>测试：{valueText(row.automated_tests, '无')}<br />证据：{listText(row.objective_evidence)}</span>
                <span>{valueText(row.task_acceptance_result)}<br />{valueText(row.stage_acceptance_result)}</span>
              </article>
            ))}
          </div>
        ) : <EmptyState text="暂无返修任务。" />}
      </section>

      <section className="v3-section wide">
        <header className="v3-section-head"><h2>{terminalProject ? '历史人工验收证据' : '人工验收证据'}</h2><span>{terminalProject ? '历史证据仅供审计，不产生当前用户动作。' : '仅在当前项目需要人工证据时提交。'}</span></header>
        {evidenceMessage ? <div className="v3-note"><p>{evidenceMessage}</p></div> : null}
        {!terminalProject ? <form className="v3-evidence-form" onSubmit={(event) => void submitEvidence(event)}>
          <label>证据标题<input value={evidenceTitle} onChange={(event) => setEvidenceTitle(event.target.value)} placeholder="例如：RC2 微信开发者工具导入与编译截图" required /></label>
          <label>验收说明<textarea value={evidenceDescription} onChange={(event) => setEvidenceDescription(event.target.value)} placeholder="说明 AppID、导入、编译、页面和闭环操作结果。不要填写密码或密钥。" /></label>
          <label>操作步骤<textarea value={evidenceSteps} onChange={(event) => setEvidenceSteps(event.target.value)} placeholder="每行一步：导入工程、编译、完成摸底、查看报告..." /></label>
          <label>截图路径或链接<textarea value={evidenceScreenshots} onChange={(event) => setEvidenceScreenshots(event.target.value)} placeholder="每行一个截图路径或证据链接" /></label>
          <label>日志摘要<textarea value={evidenceLogs} onChange={(event) => setEvidenceLogs(event.target.value)} placeholder="每行一条编译日志、控制台结论或测试结果" /></label>
          <label>产物路径<textarea value={evidenceArtifacts} onChange={(event) => setEvidenceArtifacts(event.target.value)} placeholder="每行一个 app.json、project.config.json、测试报告或导入说明路径" /></label>
          <label>覆盖的验收项<textarea value={evidenceAcceptanceItems} onChange={(event) => setEvidenceAcceptanceItems(event.target.value)} placeholder="必须填写该返修任务的具体验收项，每行一项；不能用笼统证据批量通过。" required /></label>
          <label>关联问题 ID<input value={evidenceRelatedIssues} onChange={(event) => setEvidenceRelatedIssues(event.target.value)} placeholder="例如 issue-8bc6df7dd97d" /></label>
          <label>关联返修任务 ID<input value={evidenceRelatedTask} onChange={(event) => setEvidenceRelatedTask(event.target.value)} placeholder="只能填写一个具体 task_id" required /></label>
          <button className="primary-button" disabled={!evidenceTitle.trim() || !evidenceAcceptanceItems.trim() || !evidenceRelatedTask.trim() || !!busyEvidence}>{busyEvidence === 'submit' ? '提交中…' : '提交人工证据'}</button>
        </form> : null}
        {!terminalProject ? <label className="v3-review-reason">审核意见/驳回原因<input value={reviewReason} onChange={(event) => setReviewReason(event.target.value)} placeholder="审核通过或退回时保存到审核历史" /></label> : null}
        {formalManualEvidence.length ? formalManualEvidence.map((item) => {
          const evidence = asRecord(item);
          const evidenceId = valueText(evidence.id, '');
          const reviewed = ['approved', 'rejected'].includes(valueText(evidence.review_status, ''));
          const history = asList(evidence.review_history);
          return (
            <article className="v3-issue-row" key={evidenceId}>
              <header className="v3-issue-head">
                <div>
                  <strong>{valueText(evidence.title, '人工验收证据')}</strong>
                  <span>{valueText(evidence.version, 'RC2')} · {fmtTime(evidence.submitted_at as string | null)}</span>
                </div>
                <em>{valueText(evidence.review_result || evidence.review_status, '等待审核')}</em>
              </header>
              <DetailGrid rows={[
                ['说明', evidence.description],
                ['提交状态', evidence.submission_status || evidence.status],
                ['审核状态', evidence.review_status || 'pending'],
                ['覆盖验收项', listText(evidence.acceptance_items)],
                ['操作步骤', listText(evidence.operation_steps)],
                ['截图', listText(evidence.screenshots)],
                ['日志', listText(evidence.logs)],
                ['产物路径', listText(evidence.artifact_paths)],
                ['控制台错误', listText(evidence.console_errors)],
                ['关联问题', listText(evidence.related_issue_ids)],
                ['关联返修任务', listText(evidence.related_repair_task_ids)],
                ['审核原因', evidence.review_reason],
                ['审核人', evidence.reviewer],
                ['审核时间', fmtTime(evidence.reviewed_at as string | null)],
                ['审核历史', history.map((row) => `${valueText(row.reviewed_at)} ${valueText(row.reviewer)} ${valueText(row.result)}：${valueText(row.reason || row.opinion)}`).join('；') || '暂无'],
              ]} />
              <div className="v3-entry-row">
                <button className="ghost-button" disabled={terminalProject || busyEvidence === evidenceId || reviewed} onClick={() => void reviewEvidence(evidenceId, 'approve')}>审核通过</button>
                <button className="danger-button" disabled={terminalProject || busyEvidence === evidenceId || reviewed} onClick={() => void reviewEvidence(evidenceId, 'reject')}>退回补充</button>
              </div>
            </article>
          );
        }) : <CompactEmpty text="暂无正式人工验收证据。" />}
        {systemTestEvidence.length ? (
          <section className="v3-system-test-records">
            <header className="v3-section-head"><h2>系统测试记录</h2><span>测试数据，不计入正式验收。</span></header>
            {systemTestEvidence.map((item) => (
              <article className="v3-issue-row" key={valueText(item.id)}>
                <header className="v3-issue-head">
                  <div>
                    <strong>{valueText(item.title, '系统测试记录')}</strong>
                    <span>{valueText(item.evidence_scope, 'test_only')} / {valueText(item.evidence_kind, 'production_smoke')}</span>
                  </div>
                  <em>测试数据，不计入正式验收</em>
                </header>
                <DetailGrid rows={[
                  ['提交状态', item.submission_status || item.status],
                  ['当前状态', item.review_status],
                  ['排除原因', item.exclusion_reason || '生产冒烟或系统测试数据'],
                  ['关联返修任务', listText(item.related_repair_task_ids)],
                  ['历史保留', `${asList(item.review_history).length} 条审核历史`],
                ]} />
              </article>
            ))}
          </section>
        ) : null}
      </section>

      <section className="v3-section wide">
        <header className="v3-section-head"><h2>{terminalProject ? '当前需要处理的问题' : '仍未解决问题'}</h2><span>{terminalProject ? '当前项目已完成；历史问题不覆盖当前状态。' : '未解决问题会阻断候选交付。'}</span></header>
        {unresolved.length ? unresolved.map((issue) => (
          <p key={valueText(issue.issue_id)}><strong>{valueText(issue.title)}</strong>：{valueText(issue.next_action)}</p>
        )) : <CompactEmpty text="暂无阻断问题。" />}
      </section>
    </PageShell>
  );
}

export function V3ProjectRepairsPage() {
  const { projectId = '' } = useParams();
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [repairTab, setRepairTab] = useState<'requirements' | 'execution' | 'result' | 'evidence'>('result');
  const [refreshKey, setRefreshKey] = useState(0);
  const [repairResultSummary, setRepairResultSummary] = useState('');
  const [repairResultFiles, setRepairResultFiles] = useState('');
  const [repairResultDiff, setRepairResultDiff] = useState('');
  const [repairResultArtifacts, setRepairResultArtifacts] = useState('');
  const [repairResultTests, setRepairResultTests] = useState('');
  const [repairResultPreview, setRepairResultPreview] = useState('');
  const [repairMessage, setRepairMessage] = useState('');
  const [busyRepair, setBusyRepair] = useState(false);
  const { loading, error, data } = useLoad<Record<string, unknown>>(() => v3Api.projectRepairs(projectId), [projectId, refreshKey]);
  const tasks = asList(data?.tasks);
  const summary = asRecord(data?.summary);
  const rcCompare = asRecord(data?.rc_compare);
  const projectView = asRecord(data?.project_user_view);
  const terminalProject = valueText(projectView.project_status).toLowerCase() === 'accepted_delivered';
  const selectedTask = asRecord(tasks.find((item) => valueText(item.task_id, '') === selectedTaskId) || tasks[0]);
  const listText = (value: unknown, fallback = '暂无') => {
    const items = textList(value);
    return items.length ? items.join('；') : valueText(value, fallback);
  };
  const statusLabel = (kind: string, value: unknown) => {
    const text = valueText(value, '');
    const maps: Record<string, Record<string, string>> = {
      execution: {
        waiting: '等待执行',
        running: '正在执行',
        succeeded: '执行完成',
        failed: '执行失败',
        manual_required: '需要人工执行',
      },
      review: {
        no_evidence: '无证据',
        pending: '证据待审核',
        approved: '审核通过',
        rejected: '审核驳回',
      },
      mode: {
        vps_executor: 'VPS 执行器',
        manual: '人工执行',
        future_local_executor: '后续本地执行节点',
      },
    };
    return maps[kind]?.[text] || valueText(value);
  };
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <EmptyState text="没有找到返修执行数据。" />;
  const manualEvidence = asList(selectedTask.manual_evidence);
  const formalEvidence = manualEvidence.filter((item) => valueText(item.evidence_scope, 'formal') !== 'test_only' && valueText(item.evidence_kind, 'manual') !== 'production_smoke' && valueText(item.excluded_from_rc2_acceptance) !== 'true');
  const systemEvidence = manualEvidence.filter((item) => !formalEvidence.includes(item));
  const reviewHistory = asList(selectedTask.review_history);
  const repairResult = asRecord(selectedTask.repair_result);
  const repairResultHistory = asList(selectedTask.repair_result_history);
  const actions = asList(selectedTask.allowed_actions);
  const missingItems = textList(selectedTask.missing_acceptance_items);
  const splitLines = (value: string) => value.split(/\n|；|;/).map((item) => item.trim()).filter(Boolean);
  const submitRepairResult = async (event: FormEvent) => {
    event.preventDefault();
    const taskId = valueText(selectedTask.task_id, '');
    if (!taskId || !repairResultSummary.trim()) return;
    setBusyRepair(true);
    setRepairMessage('');
    try {
      await v3Api.submitRepairResult(projectId, taskId, {
        summary: repairResultSummary.trim(),
        modified_files: splitLines(repairResultFiles),
        git_diff_summary: splitLines(repairResultDiff),
        artifacts: splitLines(repairResultArtifacts),
        build_and_test_result: repairResultTests.trim(),
        preview: repairResultPreview.trim(),
        submitter: 'user',
      });
      setRepairResultSummary('');
      setRepairResultFiles('');
      setRepairResultDiff('');
      setRepairResultArtifacts('');
      setRepairResultTests('');
      setRepairResultPreview('');
      setRepairMessage('修复结果已提交，等待该任务验收证据审核。');
      setRefreshKey((value) => value + 1);
    } catch (err) {
      setRepairMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyRepair(false);
    }
  };
  return (
    <PageShell eyebrow="返修执行端" title={valueText(data.title, '返修工作台')} description={terminalProject ? '项目已完成；历史返修与验收记录仅供审计。' : '查看当前返修任务、修复结果和验收证据。'}>
      <section className="v3-product-hero compact">
        <div>
          <span className="v3-eyebrow">{valueText(data.product_type, '产品')}</span>
          <h2>{terminalProject ? '返修历史（只读）' : '返修工作台'}</h2>
          <p>{valueText(data.rc1_failure_reason)}</p>
        </div>
        <dl>
          <div><dt>返修任务</dt><dd>{valueText(summary.repair_task_count, '0')}</dd></div>
          <div><dt>已审核通过</dt><dd>{valueText(summary.approved_count, '0')}</dd></div>
          <div><dt>证据不足</dt><dd>{valueText(summary.evidence_insufficient_count, '0')}</dd></div>
          <div><dt>当前项目状态</dt><dd>{valueText(projectView.user_visible_project_status, valueText(data.status, '状态暂时无法识别'))}</dd></div>
          <div><dt>当前返修状态</dt><dd>{terminalProject ? '无当前返修动作' : valueText(summary.rc2_user_trial_allowed) === 'true' ? '可进入用户试用' : '仍需处理'}</dd></div>
        </dl>
        <div className="v3-next-step">
          <span>下一步</span>
          <strong>{terminalProject ? '项目已完成，无当前返修动作' : valueText(summary.next_step)}</strong>
          <Link className="ghost-button" to={`/v3/projects/${projectId}/issues`}>返回问题中心</Link>
        </div>
      </section>

      <section className="v3-section wide">
        <header className="v3-section-head"><h2>{terminalProject ? '历史返修任务' : '返修任务列表'}</h2><span>执行完成和验收通过分开显示。</span></header>
        <div className="v3-repair-list">
          {tasks.length ? tasks.map((item) => {
            const taskId = valueText(item.task_id, '');
            const active = taskId === valueText(selectedTask.task_id, '');
            return (
              <button
                type="button"
                className={`v3-repair-row ${active ? 'active' : ''}`}
                key={taskId}
                onClick={() => { setSelectedTaskId(taskId); setRepairTab('result'); }}
              >
                <strong>{valueText(item.title, '返修任务')}</strong>
                <span>{statusLabel('mode', item.execution_mode)}</span>
                <span>{statusLabel('execution', item.execution_status)}</span>
                <span>{statusLabel('review', item.review_status)}</span>
                <em>{shortText(item.rc2_impact, '等待返修证据', 80)}</em>
              </button>
            );
          }) : <CompactEmpty text="暂无返修任务。" />}
        </div>
      </section>

      <section className="v3-section wide">
        <header className="v3-section-head"><h2>单任务执行详情</h2><span>{valueText(selectedTask.task_id, '未选择任务')}</span></header>
        {Object.keys(selectedTask).length ? (
          <>
            <div className="v3-repair-status-strip">
              <span>执行方式：{statusLabel('mode', selectedTask.execution_mode)}</span>
              <span>执行状态：{statusLabel('execution', selectedTask.execution_status)}</span>
              <span>审核状态：{statusLabel('review', selectedTask.review_status)}</span>
              <span>负责人：{valueText(selectedTask.owner)}</span>
            </div>
            <div className="v3-tabs compact">
              {[
                ['result', '修复结果'],
                ['requirements', '修复要求'],
                ['execution', '执行记录'],
                ['evidence', '验收证据'],
              ].map(([key, label]) => (
                <button key={key} className={repairTab === key ? 'active' : ''} onClick={() => setRepairTab(key as typeof repairTab)}>{label}</button>
              ))}
            </div>
            {repairTab === 'requirements' ? (
              <div className="v3-repair-detail-panel">
                <div><dt>RC1 问题来源</dt><dd>{valueText(selectedTask.rc1_issue_source)}</dd></div>
                <div><dt>当前实际结果</dt><dd>{valueText(selectedTask.current_actual_result)}</dd></div>
                <div><dt>期望结果</dt><dd>{valueText(selectedTask.expected_result)}</dd></div>
                <div><dt>阻断原因</dt><dd>{valueText(selectedTask.blocking_reason)}</dd></div>
                <div><dt>对应需求</dt><dd>{valueText(selectedTask.original_requirement)}</dd></div>
                <div><dt>验收标准</dt><dd>{listText(selectedTask.acceptance_criteria)}</dd></div>
                <div><dt>缺失验收项</dt><dd>{missingItems.length ? missingItems.join('；') : '暂无'}</dd></div>
              </div>
            ) : null}
            {repairTab === 'execution' ? (
              <div className="v3-repair-detail-panel">
                <div><dt>execution_run_id</dt><dd>{valueText(selectedTask.execution_run_id, '无；本次为人工执行记录')}</dd></div>
                <div><dt>manual_execution_id</dt><dd>{valueText(selectedTask.manual_execution_id, '暂无')}</dd></div>
                <div><dt>实际执行模型/方式</dt><dd>{modelDisplayName(selectedTask.actual_model)}</dd></div>
                <div><dt>开始时间</dt><dd>{fmtTime(selectedTask.started_at as string | null)}</dd></div>
                <div><dt>结束时间</dt><dd>{fmtTime(selectedTask.ended_at as string | null)}</dd></div>
                <div><dt>日志摘要</dt><dd>{valueText(selectedTask.log_summary)}</dd></div>
                <div><dt>失败原因</dt><dd>{valueText(selectedTask.failure_reason)}</dd></div>
              </div>
            ) : null}
            {repairTab === 'result' ? (
              <>
                <div className="v3-repair-detail-panel">
                  <div><dt>修复结果说明</dt><dd>{valueText(selectedTask.fix_result_preview)}</dd></div>
                  <div><dt>修复结果状态</dt><dd>{valueText(selectedTask.fix_result_status)}</dd></div>
                  <div><dt>修改文件</dt><dd>{listText(selectedTask.modified_files)}</dd></div>
                  <div><dt>Git diff 摘要</dt><dd>{listText(selectedTask.git_diff_summary)}</dd></div>
                  <div><dt>产物</dt><dd>{listText(selectedTask.artifacts)}</dd></div>
                  <div><dt>构建和测试结果</dt><dd>{valueText(selectedTask.build_and_test_result)}</dd></div>
                  <div><dt>对 RC2 的影响</dt><dd>{valueText(selectedTask.rc2_impact)}</dd></div>
                  <div><dt>最近提交</dt><dd>{valueText(repairResult.summary || repairResult.note)}</dd></div>
                  <div><dt>提交历史</dt><dd>{repairResultHistory.length ? repairResultHistory.map((row) => `${fmtTime(row.submitted_at as string | null)} ${valueText(row.summary)}`).join('；') : '暂无'}</dd></div>
                </div>
                <form className="v3-evidence-form compact" onSubmit={(event) => void submitRepairResult(event)}>
                  <label>修复结果摘要<input value={repairResultSummary} onChange={(event) => setRepairResultSummary(event.target.value)} placeholder="说明这一项实际修了什么，不等于验收通过" required /></label>
                  <label>修改文件<textarea value={repairResultFiles} onChange={(event) => setRepairResultFiles(event.target.value)} placeholder="每行一个文件路径" /></label>
                  <label>Git diff 摘要<textarea value={repairResultDiff} onChange={(event) => setRepairResultDiff(event.target.value)} placeholder="每行一条变更摘要" /></label>
                  <label>产物<textarea value={repairResultArtifacts} onChange={(event) => setRepairResultArtifacts(event.target.value)} placeholder="每行一个产物路径或说明" /></label>
                  <label>构建和测试结果<textarea value={repairResultTests} onChange={(event) => setRepairResultTests(event.target.value)} placeholder="例如 node --check 通过；闭环脚本弱答 18 分、复测 100 分" /></label>
                  <label>修复结果预览<input value={repairResultPreview} onChange={(event) => setRepairResultPreview(event.target.value)} placeholder="预览路径或说明，可为空" /></label>
                  <button className="primary-button" disabled={busyRepair || !repairResultSummary.trim()}>{busyRepair ? '提交中…' : '提交修复结果'}</button>
                </form>
                {repairMessage ? <div className="v3-note"><p>{repairMessage}</p></div> : null}
              </>
            ) : null}
            {repairTab === 'evidence' ? (
              <div className="v3-repair-evidence-grid">
                <section>
                  <h3>人工验收证据</h3>
                  {formalEvidence.length ? formalEvidence.map((item) => (
                    <article className="v3-mini-card" key={valueText(item.id)}>
                      <strong>{valueText(item.title, '人工证据')}</strong>
                      <p>审核状态：{valueText(item.review_status, '待审核')}</p>
                      <p>覆盖验收项：{listText(item.acceptance_items)}</p>
                      <p>截图：{listText(item.screenshots)}</p>
                      <p>日志：{listText(item.logs)}</p>
                    </article>
                  )) : <CompactEmpty text="该任务暂无正式人工验收证据。" />}
                </section>
                <section>
                  <h3>系统测试记录</h3>
                  {systemEvidence.length ? systemEvidence.map((item) => (
                    <article className="v3-mini-card" key={valueText(item.id)}>
                      <strong>{valueText(item.title, '系统测试记录')}</strong>
                      <p>测试数据，不计入正式验收。</p>
                      <p>类型：{valueText(item.evidence_scope, 'test_only')} / {valueText(item.evidence_kind, 'production_smoke')}</p>
                      <p>原审核历史：{asList(item.review_history).length} 条，完整保留。</p>
                    </article>
                  )) : <CompactEmpty text="暂无系统测试记录。" />}
                </section>
                <section>
                  <h3>审核历史</h3>
                  {reviewHistory.length ? reviewHistory.map((item, idx) => (
                    <article className="v3-mini-card" key={`${valueText(item.reviewed_at)}-${idx}`}>
                      <strong>{valueText(item.result, '审核记录')}</strong>
                      <p>{fmtTime(item.reviewed_at as string | null)} · {valueText(item.reviewer)}</p>
                      <p>{valueText(item.opinion || item.reason)}</p>
                    </article>
                  )) : <CompactEmpty text="该任务暂无正式审核历史。" />}
                </section>
              </div>
            ) : null}
            <div className="v3-entry-row">
              {actions.length ? actions.map((action) => {
                const route = valueText(action.route, '');
                const enabled = valueText(action.enabled) === 'true';
                if (route && enabled) {
                  return <Link key={valueText(action.key)} className="ghost-button" to={route}>{valueText(action.label)}</Link>;
                }
                return (
                  <button key={valueText(action.key)} className={enabled ? 'ghost-button' : 'ghost-button disabled'} disabled={!enabled} title={valueText(action.disabled_reason, '')}>
                    {valueText(action.label)}
                  </button>
                );
              }) : <span className="muted-text">暂无可执行操作。</span>}
              <Link className="primary-button" to={`/v3/projects/${projectId}/issues`}>上传/审核验收证据</Link>
            </div>
          </>
        ) : <CompactEmpty text="请选择一个返修任务。" />}
      </section>

      <section className="v3-section wide">
        <header className="v3-section-head"><h2>{terminalProject ? '历史版本与返修对比' : '候选版本与返修对比'}</h2><span>{terminalProject ? '历史记录仅供审计，不代表当前项目状态。' : '候选版本仍需真实证据，不能冒充正式交付。'}</span></header>
        <div className="v3-rc-compare-grid">
          <DetailGrid rows={[
            ['RC1 不通过原因', rcCompare.rc1_failure_reason],
            ['RC1 revision', rcCompare.rc1_revision],
            ['RC1 SHA-256', rcCompare.rc1_sha256],
            ['RC1 package SHA-256', rcCompare.rc1_package_sha256],
            ['RC2 revision', rcCompare.rc2_revision],
            ['RC2 SHA-256', rcCompare.rc2_sha256],
            ['RC2 package SHA-256', rcCompare.rc2_package_sha256],
            ['RC2 候选包路径', rcCompare.rc2_package_path],
            ['RC1/RC2 文件差异', listText(rcCompare.modified_files)],
            ['新增文件', listText(rcCompare.added_files)],
            ['新增功能', listText(rcCompare.added_features)],
            ['已通过任务', listText(rcCompare.approved_tasks)],
            ['证据不足任务', listText(rcCompare.evidence_insufficient_tasks)],
            ['被驳回任务', listText(rcCompare.rejected_tasks)],
            ['未解决问题', listText(rcCompare.unresolved_issues)],
            ['回归结果', rcCompare.regression_result],
            ['RC2 是否允许用户试用', valueText(rcCompare.rc2_user_trial_allowed) === 'true' ? '允许' : '不允许'],
          ]} />
        </div>
        <div className="v3-repair-change-list">
          {asList(rcCompare.rc2_actual_changes).map((item) => (
            <article className="v3-mini-card" key={valueText(item.task_id)}>
              <strong>{valueText(item.title, '返修项')}</strong>
              <span>{valueText(item.fix_result_status)} · {statusLabel('review', item.review_status)}</span>
              <p>文件：{listText(item.modified_files)}</p>
              <p>产物：{listText(item.artifacts)}</p>
            </article>
          ))}
        </div>
      </section>
    </PageShell>
  );
}

export function V3ProgressDetailPage({ mode }: { mode: 'project' | 'run' }) {
  const { projectId = '', runId = '' } = useParams();
  const [refreshTick, setRefreshTick] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const loader = () => (mode === 'run' ? v3Api.runProgress(runId) : v3Api.projectProgress(projectId));
  const { loading, error, data } = useLoad<Record<string, unknown>>(loader, [mode, projectId, runId, refreshTick]);
  useEffect(() => {
    if (!data) return undefined;
    const seconds = Math.max(3, Number(data.polling_seconds || 5));
    const timer = window.setInterval(() => setRefreshTick((tick) => tick + 1), seconds * 1000);
    return () => window.clearInterval(timer);
  }, [data?.polling_seconds, data]);
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <EmptyState text="没有找到实时进度。" />;
  const runs = asList(data.runs);
  const generatedFiles = Array.isArray(data.generated_files) ? data.generated_files : [];
  const artifacts = (Array.isArray(data.artifacts) ? data.artifacts : generatedFiles).map(artifactText).filter((item): item is string => Boolean(item));
  const current = asRecord(data.current_task);
  const projectView = asRecord(data.project_user_view);
  const projectProgress = asRecord(data.project_progress || data.stage_progress);
  const stageProgress = asRecord(data.stage_progress);
  const taskStepProgress = asRecord(data.task_step_progress);
  const projectPct = percent(projectProgress.percent);
  const stagePct = percent(stageProgress.percent);
  const taskStepPct = percent(taskStepProgress.percent);
  const projectedProjectStatus = valueText(projectView.user_visible_project_status, '');
  const currentStatus = statusText(
    mode === 'project' && projectedProjectStatus ? projectedProjectStatus : data.current_status || data.current_step || current.status,
    '等待下一步',
  );
  const latestLog = safeHumanText(data.latest_progress || current.latest_log || data.blocking_reason, '暂无最新进展', 180);
  const actualModel = modelDisplayName(safeHumanText(data.actual_model || current.model, '已记录在诊断详情', 80));
  const isFailed = /失败|failed|error/i.test(`${currentStatus} ${data.blocking_reason} ${current.raw_status}`);
  const retryTask = async () => {
    const taskId = valueText(data.task_id, '');
    if (!taskId) return;
    setRetrying(true);
    try {
      await tasksApi.retry(taskId);
      setRefreshTick((tick) => tick + 1);
    } finally {
      setRetrying(false);
    }
  };
  const executionSteps = asList(data.execution_steps);
  const stepLabels = executionSteps.length
    ? executionSteps.map((step) => ({
      label: safeHumanText(step.label, '执行步骤', 96),
      status: statusText(step.status, '尚未开始'),
    }))
    : runs.length
    ? runs.slice(0, 5).map((run, idx) => ({
      label: safeHumanText(run.latest_log, statusText(run.status, `执行记录 ${idx + 1}`), 96),
      status: statusText(run.status),
    }))
    : [
      { label: '读取项目任务和最近执行记录', status: '已完成' },
      { label: currentStatus, status: data.blocking_reason ? '卡住' : '当前步骤' },
      { label: '等待后续执行结果或人工验收', status: '尚未开始' },
    ];
  return (
    <PageShell eyebrow="实时进度" title={valueText(data.title, '实时进度')} description="观察当前执行到了哪一步、有没有真实进展、卡在哪里。">
      <section className="v3-progress-dashboard">
        <div className="v3-progress-summary">
          <ProgressMini label={`项目完成 ${projectProgress.label || `${projectPct}%`}`} percentValue={projectPct} />
          <ProgressMini label={`当前阶段 ${stageProgress.label || `${stagePct}%`}`} percentValue={stagePct} />
          <ProgressMini label={`当前任务步骤 ${taskStepProgress.label || `${taskStepPct}%`}`} percentValue={taskStepPct} />
          <div className="v3-current-task-box">
            <span>当前状态</span>
            <strong>{currentStatus}</strong>
            <p>{shortText(latestLog, '暂无进展')}</p>
          </div>
        </div>
        <dl className="v3-compact-facts">
          <div><dt>实际模型</dt><dd>{actualModel}</dd></div>
          <div><dt>已运行时间</dt><dd>{valueText(data.elapsed_hint, '按最近更新时间估算')}</dd></div>
          <div><dt>最近有效进展</dt><dd>{fmtTime((data.updated_at || current.updated_at || current.started_at) as string | null)}</dd></div>
          <div><dt>等待对象</dt><dd>{valueText(data.waiting_for, '暂无等待')}</dd></div>
        </dl>
        {isFailed ? (
          <div className="v3-failure-actions">
            <a className="ghost-button" href="#v3-progress-diagnostics">查看失败原因</a>
            <button className="ghost-button" onClick={() => void retryTask()} disabled={!data.task_id || retrying} title={!data.task_id ? '当前执行记录没有可重试的任务对象。' : ''}>{retrying ? '正在重新执行…' : '重新执行'}</button>
            <Link className="ghost-button" to="/v3/settings/routing">更换模型</Link>
            <Link className="ghost-button" to={data.project_id ? `/project/${data.project_id}` : '/tasks'}>修改任务</Link>
          </div>
        ) : null}
      </section>

      <div className="v3-detail-columns">
        <section className="v3-section">
          <header className="v3-section-head"><h2>执行步骤</h2><span>不伪造内部百分比，用步骤位置表达。</span></header>
          <ol className="v3-step-list">
            {stepLabels.map((step, idx) => (
              <li key={`${idx}-${step.label}`} className={/当前|失败|running|pending|queued|卡住/i.test(step.status) ? 'active' : /完成|completed|accepted/i.test(step.status) ? 'done' : ''}>
                <strong>{shortText(step.label, `步骤 ${idx + 1}`, 72)}</strong>
                <span>{step.status}</span>
              </li>
            ))}
          </ol>
        </section>
        <section className="v3-section">
          <header className="v3-section-head"><h2>最新进展</h2><span>人话版。</span></header>
          <p>{shortText(latestLog, '暂无最新进展', 180)}</p>
          {data.blocking_reason ? <p className="v3-blocking">等待或错误原因：{valueText(data.blocking_reason)}</p> : null}
        </section>
      </div>

      <div className="v3-detail-columns">
        <section className="v3-section"><h2>最近日志</h2>
          {Array.isArray(data.recent_logs) && data.recent_logs.length ? data.recent_logs.slice(-8).map((line, idx) => <p key={`${idx}-${line}`}>{safeHumanText(line, '日志已记录，详情见诊断。', 160)}</p>) : <CompactEmpty text="暂无日志。" />}
        </section>
        <section className="v3-section"><h2>修改文件</h2>{Array.isArray(data.changed_files) && data.changed_files.length ? data.changed_files.map((file) => <p key={String(file)}>{String(file)}</p>) : <CompactEmpty text="暂无文件记录。" />}</section>
      </div>
      <section className="v3-section wide"><h2>生成产物</h2>{artifacts.length ? artifacts.slice(0, 12).map((item) => <p key={item}>{item}</p>) : <CompactEmpty text="暂无可展示产物，内部执行元数据在诊断详情中。" />}</section>
      <div id="v3-progress-diagnostics"><Diagnostics value={(data.diagnostics || {}) as Record<string, unknown>} /></div>
    </PageShell>
  );
}

export function V3AcceptanceDetailPage({ kind }: { kind: 'tasks' | 'stages' | 'projects' }) {
  const { projectId } = useParams();
  if (!projectId) return <V3CenterPage center="acceptance" />;
  const [refreshKey, setRefreshKey] = useState(0);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState('');
  const { loading, error, data } = useLoad<Record<string, unknown>>(() => v3Api.acceptanceDetail(kind, projectId), [kind, projectId, refreshKey]);
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <EmptyState text="没有找到验收详情。" />;
  const criteria = asList(data.criteria);
  const evidence = asList(data.evidence);
  const recentRuns = asList(data.recent_runs);
  const projectView = asRecord(data.project_user_view);
  const terminalProject = valueText(projectView.project_status, valueText(data.raw_acceptance_status)).toLowerCase() === 'accepted_delivered';
  const currentProjectStatus = valueText(projectView.user_visible_project_status, valueText(data.acceptance_status));
  const failed = criteria.filter((item) => !/通过|已通过|accepted|completed|完成/i.test(`${item.actual_result} ${item.result_reason}`));
  const allPassed = criteria.length > 0 && failed.length === 0;
  const flowState = valueText(data.acceptance_flow_state, allPassed ? 'stages_passed' : 'not_ready');
  const nextLabel = terminalProject || flowState === 'completed'
    ? '项目已完成，无需操作'
    : flowState === 'candidate_ready'
    ? '进入交付中心'
    : flowState === 'project_approved'
      ? '生成候选交付'
      : flowState === 'final_reviewing'
        ? '确认项目验收结果'
        : flowState === 'stages_passed'
          ? '开始项目总验收'
          : '处理未完成任务';
  const runFinalAction = async (action: 'review' | 'approve' | 'reject' | 'package') => {
    setBusy(action);
    setMessage('');
    try {
      if (action === 'review') await v3Api.startFinalDeliveryReview(projectId);
      if (action === 'approve') await v3Api.approveFinalDelivery(projectId);
      if (action === 'reject') await v3Api.rejectFinalDelivery(projectId, 'V3 项目验收未通过，创建返修任务。');
      if (action === 'package') await v3Api.planDeliveryPackage(projectId);
      setMessage(action === 'review' ? '项目总验收已开始。' : action === 'approve' ? '项目验收已确认通过。' : action === 'package' ? '候选交付已生成，可以进入交付中心。' : '已记录未通过原因，请创建返修任务。');
      setRefreshKey((x) => x + 1);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy('');
    }
  };
  return (
    <PageShell eyebrow="验收中心" title={valueText(data.title, '项目验收')} description="确认这个项目是否真的达到交付标准。">
      <section className="v3-product-hero compact">
        <div>
          <span className="v3-eyebrow">{valueText(data.product_type, '产品')}</span>
          <h2>{valueText(data.title, '项目验收')}</h2>
          <p>{valueText(data.summary, allPassed ? '阶段和任务均已通过，可以开始项目总验收。' : '仍有未通过或未确认项目。')}</p>
        </div>
        <dl>
          <div><dt>当前项目状态</dt><dd>{currentProjectStatus}</dd></div>
          <div><dt>验收流程</dt><dd>{valueText(data.acceptance_status)}</dd></div>
          <div><dt>交付流程</dt><dd>{valueText(data.delivery_status)}</dd></div>
          <div><dt>未通过项</dt><dd>{failed.length}</dd></div>
        </dl>
        <div className="v3-next-step">
          <span>下一步</span>
          <strong>{nextLabel}</strong>
          {flowState === 'stages_passed' ? (
            <button className="primary-button" disabled={!allPassed || !!busy} onClick={() => void runFinalAction('review')}>{busy === 'review' ? '处理中…' : '开始项目总验收'}</button>
          ) : null}
          {flowState === 'final_reviewing' ? (
            <div className="v3-entry-row">
              <button className="primary-button" disabled={!!busy} onClick={() => void runFinalAction('approve')}>{busy === 'approve' ? '处理中…' : '确认通过'}</button>
              <button className="danger-button" disabled={!!busy} onClick={() => void runFinalAction('reject')}>创建返修任务</button>
            </div>
          ) : null}
          {flowState === 'project_approved' ? (
            <button className="primary-button" disabled={!!busy} onClick={() => void runFinalAction('package')}>{busy === 'package' ? '处理中…' : '生成候选交付'}</button>
          ) : null}
          {flowState === 'candidate_ready' ? <Link className="primary-button" to={`/v3/delivery/${projectId}`}>进入交付中心</Link> : null}
          {flowState === 'not_ready' ? <Link className="ghost-button" to={`/v3/projects/${projectId}`}>查看项目执行</Link> : null}
          <Link className="ghost-button" to={`/v3/projects/${projectId}/quality`}>质量审计</Link>
        </div>
      </section>
      {message ? <div className="v3-note"><p>{message}</p></div> : null}

      <section className="v3-section wide">
        <header className="v3-section-head"><h2>验收标准与实际结果</h2><span>内部字段名已移入诊断，只保留可判断的信息。</span></header>
        {criteria.length ? (
          <div className="v3-acceptance-list">
            {criteria.map((item, idx) => (
              <article className="v3-acceptance-row" key={`${idx}-${valueText(item.criterion)}`}>
                <div>
                  <strong>{safeHumanText(item.object, `验收项 ${idx + 1}`, 72)}</strong>
                  <p>{safeHumanText(item.criterion, '按交付标准逐项检查', 96)}</p>
                </div>
                <span>{safeHumanText(item.expected_result, '应满足验收标准', 80)}</span>
                <span>{safeHumanText(item.actual_result, '待确认实际结果', 80)}</span>
                <span>{evidenceText(item.evidence)}</span>
              </article>
            ))}
          </div>
        ) : <EmptyState text="暂无验收标准。" />}
      </section>

      <div className="v3-detail-columns">
        <section className="v3-section">
          <h2>未通过项目</h2>
          {failed.length ? failed.slice(0, 10).map((item, idx) => <p key={`${idx}-${valueText(item.object)}`}><strong>{valueText(item.object)}</strong>：{valueText(item.result_reason || item.actual_result)}</p>) : <EmptyState text="暂无未通过项。" />}
        </section>
        <section className="v3-section">
          <h2>执行证据</h2>
          {evidence.length ? evidence.slice(0, 12).map((item, idx) => (
            <p key={`${idx}-${valueText(item.title)}`}><strong>{valueText(item.type)}</strong>：{valueText(item.title)} · {safeHumanText(item.summary, '证据已记录', 120)}</p>
          )) : recentRuns.length ? recentRuns.map((run) => <p key={valueText(run.run_id)}>{runLine(run)}</p>) : <CompactEmpty text="暂无执行证据。" />}
        </section>
      </div>
      <Diagnostics value={(data.diagnostics || {}) as Record<string, unknown>} />
    </PageShell>
  );
}

export function V3DeliveryDetailPage() {
  const { projectId = '' } = useParams();
  const [refreshKey, setRefreshKey] = useState(0);
  const [issueTitle, setIssueTitle] = useState('');
  const [issueDescription, setIssueDescription] = useState('');
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState('');
  const { loading, error, data } = useLoad<Record<string, unknown>>(() => v3Api.deliveryDetail(projectId), [projectId, refreshKey]);
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <EmptyState text="没有找到交付详情。" />;
  const files = asList(data.delivery_files);
  const artifacts = asList(data.artifact_tasks);
  const knownIssues = Array.isArray(data.known_issues) ? data.known_issues : [];
  const repairTasks = Array.isArray(data.repair_tasks) ? data.repair_tasks : [];
  const repairSummary = asRecord(data.repair_summary);
  const notes = Array.isArray(data.notes) ? data.notes : [];
  const cleanFiles = files.filter((file) => !excludedDeliveryPath.test(valueText(file.path)));
  const deployment = asRecord(data.deployment);
  const projectView = asRecord(data.project_user_view);
  const terminalProject = valueText(projectView.project_status).toLowerCase() === 'accepted_delivered';
  const formalVersion = valueText(data.current_version, '');
  const versionLabel = formalVersion ? (formalVersion.startsWith('RC') ? '候选版本' : '正式版本') : '正式版本号';
  const versionValue = formalVersion || '未记录正式版本号';
  const productUrl = valueText(data.product_url || deployment.product_url, '');
  const openIssueIds = knownIssues
    .filter((item) => ['open', 'repair_requested'].includes(valueText(asRecord(item).status)))
    .map((item) => valueText(asRecord(item).id))
    .filter(Boolean);
  const submitIssue = async (event: FormEvent) => {
    event.preventDefault();
    const title = issueTitle.trim();
    if (!title || busy) return;
    setBusy('issue');
    setMessage('');
    try {
      await v3Api.submitDeliveryIssue(projectId, { title, description: issueDescription.trim(), severity: 'major' });
      setIssueTitle('');
      setIssueDescription('');
      setMessage('问题已提交，候选交付已进入返修状态。');
      setRefreshKey((value) => value + 1);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy('');
    }
  };
  const createRepair = async () => {
    if (!openIssueIds.length || busy) return;
    setBusy('repair');
    setMessage('');
    try {
      await v3Api.createDeliveryRepairTask(projectId, {
        issue_ids: openIssueIds,
        title: '返修：补齐候选交付缺失的学习产品闭环',
        note: '本次只创建返修任务，不手工生成 RC2。',
      });
      setMessage('返修任务已创建，等待执行后生成下一版候选交付。');
      setRefreshKey((value) => value + 1);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy('');
    }
  };
  const fileGroups = [
    ['可运行成品', cleanFiles.filter((file) => /可运行成品|构建包|build-package|index\.html|dist|build|app/i.test(`${file.kind} ${file.path}`))],
    ['源代码', cleanFiles.filter((file) => /源代码|source-code|\.(tsx?|jsx?|py|css|html)$/i.test(`${file.kind} ${file.path}`))],
    ['配置与环境说明', cleanFiles.filter((file) => /配置|环境|README|说明/i.test(`${file.kind} ${file.path}`))],
    ['测试报告', cleanFiles.filter((file) => /测试报告|TEST_REPORT|test|report|coverage/i.test(`${file.kind} ${file.path}`))],
    ['部署说明', cleanFiles.filter((file) => /部署说明|DEPLOYMENT|deploy|docker|compose|nginx/i.test(`${file.kind} ${file.path}`))],
    ['正式交付包', cleanFiles.filter((file) => /\.zip|release|正式交付包/i.test(`${file.kind} ${file.path}`) && !/package-lock/i.test(valueText(file.path)))],
  ] as const;
  const packagePlan = asRecord(data.packaging_plan);
  return (
    <PageShell eyebrow="交付中心" title={valueText(data.title, '交付详情')} description="检查成品、部署、文件、版本和返修，确认能交到用户手里。">
      <section className="v3-product-hero compact">
        <div>
          <span className="v3-eyebrow">{valueText(data.product_type, '产品')}</span>
          <h2>{valueText(data.title, '候选交付')}</h2>
          <p>{shortText(data.final_product, '以最新通过验收的任务产物为当前成品。', 150)}</p>
        </div>
        <dl>
          <div><dt>{versionLabel}</dt><dd>{versionValue}</dd></div>
          <div><dt>当前项目状态</dt><dd>{valueText(projectView.user_visible_project_status, '状态暂时无法识别')}</dd></div>
          <div><dt>项目验收</dt><dd>{valueText(data.project_acceptance)}</dd></div>
          <div><dt>交付流程</dt><dd>{valueText(data.status)}</dd></div>
          <div><dt>生成时间</dt><dd>{fmtTime(data.generated_at as string | null)}</dd></div>
          <div><dt>项目 revision</dt><dd className="v3-hash-value">{valueText(data.project_revision, '未记录')}</dd></div>
          <div><dt>交付包 SHA-256</dt><dd className="v3-hash-value">{valueText(data.package_sha256, '尚未生成')}</dd></div>
        </dl>
        <div className="v3-next-step">
          <span>交付动作</span>
          <div className="v3-entry-row">
            {data.preview_entry ? <a className="primary-button" href={String(data.preview_entry)} target="_blank" rel="noreferrer">打开完整预览</a> : <button className="ghost-button" disabled>暂无预览</button>}
            {data.package_entry ? <a className="ghost-button" href={String(data.package_entry)} target="_blank" rel="noreferrer">{String(data.current_version || '').startsWith('RC') ? '下载候选交付包' : '下载正式交付包'}</a> : <button className="ghost-button" disabled>暂无交付包</button>}
          </div>
          <div className="v3-entry-row">
            {!terminalProject ? <a className="ghost-button" href="#delivery-issues">提交问题</a> : null}
            {!terminalProject ? <button className="danger-button" disabled={!openIssueIds.length || !!busy} onClick={() => void createRepair()}>{busy === 'repair' ? '正在创建返修…' : '创建返修'}</button> : null}
            <Link className="ghost-button" to={`/v3/projects/${projectId}/issues`}>查看版本差异</Link>
          </div>
          {message ? <p className="v3-inline-message">{message}</p> : null}
        </div>
      </section>

      <div className="v3-delivery-grid">
        <section className="v3-section">
          <h2>1. 成品预览</h2>
          <DetailGrid rows={[
            ['完整产品预览', data.preview_entry ? '可打开' : '暂无'],
            ['产品访问入口', productUrl || '尚未部署到正式访问地址'],
            ['预览失败原因', data.preview_entry ? '暂无' : data.preview_failure_reason || '暂无可预览入口'],
          ]} />
          {artifacts.length ? artifacts.slice(0, 3).map((item) => <p key={valueText(item.task_id)}><strong>{valueText(item.title)}</strong>：{valueText(item.status)}</p>) : <CompactEmpty text="暂无可预览成品入口。" />}
        </section>
        <section className="v3-section">
          <h2>2. 部署与运行</h2>
          <label className="v3-compact-label">部署目标<select defaultValue={valueText(deployment.selected_target, '未选择')}>{asList(deployment.targets).length ? asList(deployment.targets).map((target) => <option key={valueText(target)}>{valueText(target)}</option>) : (Array.isArray(deployment.targets) ? deployment.targets.map((target) => <option key={String(target)}>{String(target)}</option>) : <option>未选择</option>)}</select></label>
          <DetailGrid rows={[
            ['访问地址', productUrl || '未配置产品访问地址'],
            ['部署状态', deployment.status || data.status],
            ['健康检查', deployment.health_check || packagePlan.health_check || '尚未执行'],
          ]} />
          {!deployment.can_deploy ? <p className="v3-blocking">{valueText(deployment.disabled_reason, '部署目标未配置。')}</p> : null}
          <div className="v3-entry-row"><button className="ghost-button" disabled={!deployment.can_deploy}>部署或重新部署</button><button className="ghost-button" disabled>查看部署日志</button><button className="ghost-button" disabled>回滚上一版本</button></div>
        </section>
        <section className="v3-section wide">
          <h2>3. 交付文件</h2>
          <div className="v3-file-category-grid">
            {fileGroups.map(([name, group]) => (
              <article key={name}>
                <strong>{name}</strong>
                {group.length ? group.slice(0, 8).map((file) => <span key={valueText(file.path)}>{shortText(file.path, '文件', 60)}</span>) : <em>暂无</em>}
              </article>
            ))}
          </div>
          <p className="v3-muted">已排除 node_modules、缓存、临时文件、调试日志和明显重复/映射文件。</p>
        </section>
        <section className="v3-section">
          <h2>4. 版本与变更</h2>
          <DetailGrid rows={[
            ['版本', versionValue],
            ['生成时间', fmtTime(data.generated_at as string | null)],
            ['项目 revision', data.project_revision],
            ['交付包 SHA-256', data.package_sha256],
            ['生成文件', cleanFiles.length],
            ['任务产物', artifacts.length],
          ]} />
        </section>
        <section className="v3-section" id="delivery-issues">
          <h2>5. 问题与返修</h2>
          {!terminalProject ? <form className="v3-form-stack" onSubmit={(event) => void submitIssue(event)}>
            <label>问题标题<input value={issueTitle} onChange={(event) => setIssueTitle(event.target.value)} placeholder="例如：缺少真实能力测试与出题诊断流程" /></label>
            <label>问题说明<textarea value={issueDescription} onChange={(event) => setIssueDescription(event.target.value)} placeholder="说明为什么当前候选交付不能验收，以及期望返修结果。" /></label>
            <button className="danger-button" disabled={!issueTitle.trim() || !!busy}>{busy === 'issue' ? '正在提交…' : '提交问题并退回候选交付'}</button>
          </form> : <p className="v3-muted">当前项目已完成，没有需要提交或创建的返修。</p>}
          <DetailGrid rows={[
            ['未解决问题', repairSummary.open_issue_count ?? knownIssues.length],
            ['返修任务', repairSummary.repair_task_count ?? repairTasks.length],
            ['下一步', repairSummary.next_step || '暂无未解决交付问题'],
          ]} />
          {knownIssues.length ? knownIssues.map((item) => {
            const issue = asRecord(item);
            return (
              <div className="v3-issue-row" key={valueText(issue.id)}>
                <strong>{valueText(issue.title, '交付问题')}</strong>
                <span>{valueText(issue.status, 'open')}</span>
                <DetailGrid rows={[
                  ['所属版本', issue.version || 'RC1'],
                  ['当前实际结果', issue.actual_result || issue.description || '未记录'],
                  ['期望结果', issue.expected_result || '未记录'],
                  ['复现或验证步骤', textList(issue.reproduction_steps).map((step, idx) => `${idx + 1}. ${step}`).join('；') || '未记录'],
                  ['严重程度', issue.severity || 'major'],
                  ['是否阻断交付', issue.blocks_delivery === false ? '否' : '是'],
                  ['对应原始需求', issue.original_requirement || '未记录'],
                  ['客观证据', textList(issue.evidence).join('；') || '未记录'],
                  ['当前状态', issue.status || 'open'],
                  ['关联返修任务', textList(issue.repair_task_ids || issue.repair_task_id).join('；') || '未创建'],
                ]} />
              </div>
            );
          }) : <EmptyState text="暂无未解决问题。" />}
          {repairTasks.length ? repairTasks.map((item) => {
            const task = asRecord(item);
            return (
              <div className="v3-issue-row" key={valueText(task.id)}>
                <strong>{valueText(task.title, '返修任务')}</strong>
                <span>{valueText(task.status, 'pending')}</span>
                <p>任务 ID：{valueText(task.task_id)}</p>
                <DetailGrid rows={[
                  ['对应 RC1 问题', textList(task.issue_ids).join('；') || valueText(task.related_issue_id, '未关联')],
                  ['明确产物', task.deliverable || '未记录'],
                  ['可执行验收标准', textList(task.acceptance_criteria).map((entry, idx) => `${idx + 1}. ${entry}`).join('；') || '未记录'],
                  ['客观证据要求', textList(task.evidence_required).join('；') || '未记录'],
                  ['前置依赖', textList(task.dependencies).join('；') || '无'],
                  ['完成后影响的需求', textList(task.impacted_requirements).join('；') || '未记录'],
                  ['任务验收方法', task.verification_method || '未记录'],
                ]} />
                <p>{valueText(task.next_step)}</p>
                <div className="v3-entry-row">
                  <Link className="ghost-button" to={`/v3/projects/${projectId}/repairs`}>进入返修执行</Link>
                </div>
              </div>
            );
          }) : null}
        </section>
      </div>
      <Diagnostics value={{ packaging_plan: data.packaging_plan || {}, notes, diagnostics: data.diagnostics || {} }} />
    </PageShell>
  );
}

const roleLabels: Record<string, string> = {
  clarifier: '需求理解与澄清',
  planner: '项目大纲',
  decomposer: '任务拆解',
  executor: '任务执行',
  reviewer: '验收',
  tester: '测试',
};

type ProviderOption = { provider: string; model: string; provider_kind: string; configured?: boolean; is_favorite?: boolean; source?: string };
type AccessProfile = Record<string, unknown> & { id: string; profile_type: string; name: string };

function resourceSummary(profile: AccessProfile): string {
  if (profile.profile_type === 'vps') return 'VPS 连接已配置，完整主机信息仅在编辑时显示';
  return '执行资源已配置';
}

function githubRepoName(profile: AccessProfile): string {
  const raw = valueText(profile.repo, '-').trim().replace(/[?#].*$/, '').replace(/\/+$/, '');
  if (!raw || raw === '-') return '-';
  const segment = raw.split('/').filter(Boolean).pop() || raw;
  return segment.replace(/\.git$/i, '') || '-';
}

function githubFailureReason(profile: AccessProfile): string {
  const status = valueText(profile.last_status, 'unknown');
  if (status === 'insufficient_scope') return 'GitHub Token 权限不足，请确认仓库读取权限和组织 SSO 授权。';
  if (status === 'invalid_config') return 'GitHub 配置不完整，请重新填写仓库和 Token。';
  if (status === 'invalid') return 'GitHub 授权失败或仓库不可访问，请更新 Token，并确认仓库可见性和组织 SSO 授权。';
  return '';
}

const emptyProviderForm = {
  provider_name: '',
  display_name: '',
  base_url: '',
  api_key: '',
  default_model: '',
  auth_header: 'Authorization: Bearer',
  models_endpoint: '',
};

export function V3ModelSettingsPanel() {
  const [providers, setProviders] = useState<CatalogProvider[]>([]);
  const [models, setModels] = useState<Record<string, CatalogModel[]>>({});
  const [modelInput, setModelInput] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<string>('');
  const [keyInput, setKeyInput] = useState('');
  const [baseUrlInput, setBaseUrlInput] = useState('');
  const [newProvider, setNewProvider] = useState(emptyProviderForm);
  const [showNew, setShowNew] = useState(false);
  const [onlyEnabledModels, setOnlyEnabledModels] = useState(false);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState('');
  const loadProviders = useCallback(async () => {
    const data = await providerCatalogApi.listAll();
    setProviders(data.providers.filter((p) => p.configured || p.is_custom || p.model || p.key_count > 0));
  }, []);
  useEffect(() => { void loadProviders().catch((err) => setMessage(err instanceof Error ? err.message : String(err))); }, [loadProviders]);

  const loadModels = async (provider: string) => {
    const data = await providerCatalogApi.getModels(provider);
    setModels((prev) => ({ ...prev, [provider]: data.models }));
  };
  const saveProvider = async (provider: CatalogProvider) => {
    setBusy(provider.provider);
    try {
      const payload: Record<string, string> = {};
      if (baseUrlInput) payload.base_url = baseUrlInput;
      if (keyInput) payload.api_key = keyInput;
      if (provider.model) payload.default_model = provider.model;
      if (Object.keys(payload).length) await providerCatalogApi.updateConfig(provider.provider, payload);
      setMessage(`${provider.display_name} 已更新。`);
      setEditing('');
      setKeyInput('');
      setBaseUrlInput('');
      await loadProviders();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy('');
    }
  };
  const createProvider = async (event: FormEvent) => {
    event.preventDefault();
    setBusy('new-provider');
    try {
      await providerCatalogApi.createCustom({
        provider_name: newProvider.provider_name,
        display_name: newProvider.display_name || newProvider.provider_name,
        base_url: newProvider.base_url,
        api_key: newProvider.api_key,
        default_model: newProvider.default_model || undefined,
        auth_header: newProvider.auth_header || undefined,
        models_endpoint: newProvider.models_endpoint || undefined,
      });
      setMessage('供应商已添加。');
      setNewProvider(emptyProviderForm);
      setShowNew(false);
      await loadProviders();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy('');
    }
  };
  const addModel = async (provider: string) => {
    const modelId = (modelInput[provider] || '').trim();
    if (!modelId) return;
    await providerCatalogApi.addCustomModel(provider, { model_id: modelId, is_favorite: true });
    setModelInput((prev) => ({ ...prev, [provider]: '' }));
    await loadModels(provider);
  };
  const setDefaultModel = async (provider: CatalogProvider, modelId: string) => {
    await providerCatalogApi.updateConfig(provider.provider, {
      base_url: provider.base_url || undefined,
      default_model: modelId,
    });
    setMessage(`${provider.display_name} 默认模型已设为 ${modelId}。`);
    await loadProviders();
    await loadModels(provider.provider);
  };
  const refreshProviderModels = async (provider: string) => {
    setBusy(`refresh:${provider}`);
    try {
      const result = await providerCatalogApi.refreshModels(provider);
      setMessage(result.error ? `${provider} 模型刷新完成，但连接未确认：${providerFailureText(result.error)}` : `${provider} 已刷新 ${result.model_count} 个模型。`);
      await loadModels(provider);
      await loadProviders();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy('');
    }
  };
  const testProvider = async (provider: CatalogProvider) => {
    if (!provider.model) {
      setMessage(`${provider.display_name} 尚未选择默认模型。`);
      return;
    }
    setBusy(`test:${provider.provider}`);
    try {
      const result = await providerCatalogApi.testModel(provider.provider, provider.model);
      const failure = valueText(result.failure_reason, '');
      setMessage(`${provider.display_name} 当前默认模型测试结果：${statusText(result.status, '测试完成')}${failure ? `；${providerFailureText(failure)}` : ''}`);
      await loadProviders();
      await loadModels(provider.provider);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy('');
    }
  };
  const testModel = async (provider: string, modelId: string) => {
    setBusy(`test-model:${provider}:${modelId}`);
    try {
      const result = await providerCatalogApi.testModel(provider, modelId);
      const status = valueText(result.status, 'unknown');
      const latency = typeof result.latency_ms === 'number' ? `，${result.latency_ms}ms` : '';
      const failure = valueText(result.failure_reason, '');
      setMessage(`${modelId}：${statusText(status, '测试完成')}${latency}${failure ? `；${providerFailureText(failure)}` : ''}`);
      await loadModels(provider);
      await loadProviders();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy('');
    }
  };
  const deleteProvider = async (provider: CatalogProvider) => {
    if (!window.confirm(`删除供应商 ${provider.display_name}？已保存 Key 和缓存模型会被移除，项目历史不会删除。`)) return;
    setBusy(`delete:${provider.provider}`);
    try {
      await providerCatalogApi.deleteProvider(provider.provider);
      setMessage(`${provider.display_name} 已删除。`);
      await loadProviders();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy('');
    }
  };
  const useVolcenginePreset = () => {
    setNewProvider({
      provider_name: 'volcengine',
      display_name: '火山方舟',
      base_url: 'https://ark.cn-beijing.volces.com/api/v3',
      api_key: '',
      default_model: '',
      auth_header: 'Authorization: Bearer',
      models_endpoint: 'https://ark.cn-beijing.volces.com/api/v3/models',
    });
    setShowNew(true);
  };

  return (
    <>
      <div className="v3-settings-actions">
        <button className="primary-button" onClick={() => setShowNew(true)}>添加供应商</button>
        <button className="ghost-button" onClick={useVolcenginePreset}>添加火山方舟</button>
        <button className="ghost-button" onClick={() => void loadProviders()}>刷新</button>
        <label className="v3-toggle-inline"><input type="checkbox" checked={onlyEnabledModels} onChange={(e) => setOnlyEnabledModels(e.target.checked)} />只显示启用模型</label>
      </div>
      {message ? <div className="v3-note"><p>{message}</p></div> : null}
      {showNew ? (
        <form className="v3-form-panel" onSubmit={createProvider}>
          <h2>添加供应商</h2>
          <div className="v3-form-grid">
            <label>供应商标识<input value={newProvider.provider_name} onChange={(e) => setNewProvider({ ...newProvider, provider_name: e.target.value })} required /></label>
            <label>显示名称<input value={newProvider.display_name} onChange={(e) => setNewProvider({ ...newProvider, display_name: e.target.value })} /></label>
            <label>API Base URL<input type="url" value={newProvider.base_url} onChange={(e) => setNewProvider({ ...newProvider, base_url: e.target.value })} required /></label>
            <label>API Key<input type="password" value={newProvider.api_key} onChange={(e) => setNewProvider({ ...newProvider, api_key: e.target.value })} required /></label>
            <label>默认模型<input value={newProvider.default_model} onChange={(e) => setNewProvider({ ...newProvider, default_model: e.target.value })} /></label>
            <label>模型列表接口<input value={newProvider.models_endpoint} onChange={(e) => setNewProvider({ ...newProvider, models_endpoint: e.target.value })} /></label>
          </div>
          <div className="v3-entry-row"><button className="primary-button" disabled={busy === 'new-provider'}>{busy === 'new-provider' ? '保存中…' : '保存供应商'}</button><button type="button" className="ghost-button" onClick={() => setShowNew(false)}>取消</button></div>
        </form>
      ) : null}
      <div className="v3-provider-table">
        {providers.length ? providers.map((provider) => {
          const currentModels = (models[provider.provider] || []).filter((m) => !/^hidden|provider_greenfield|hidden-provider/i.test(m.model_id));
          const enabledModels = currentModels.filter((m) => m.is_favorite || m.model_id === provider.model);
          const displayModels = onlyEnabledModels ? enabledModels : currentModels;
          const configuredModelCount = currentModels.length || (provider.model ? 1 : 0);
          const isEditing = editing === provider.provider;
          const testLabel = provider.test_status === 'test_passed' ? '测试通过' : provider.test_status === 'test_failed' ? '测试失败' : '未测试';
          return (
            <article className="v3-provider-row" key={provider.provider}>
              <header>
                <div><strong>{provider.display_name}</strong></div>
                <span className="status-pill">{provider.configured ? '已配置' : '未配置'}</span>
              </header>
              <dl>
                <div><dt>配置状态</dt><dd>{provider.configured ? '已配置' : '未配置'}</dd></div>
                <div><dt>当前模型</dt><dd>{provider.model || '未选择'}</dd></div>
                <div><dt>连接测试</dt><dd>{testLabel}</dd></div>
                <div><dt>最近测试时间</dt><dd>{fmtTime(provider.last_tested_at || null)}</dd></div>
                <div><dt>失败原因</dt><dd>{provider.test_status === 'test_failed' ? providerFailureText(provider.failure_reason) : '暂无'}</dd></div>
                <div><dt>已配置模型</dt><dd>{configuredModelCount}</dd></div>
              </dl>
              {isEditing ? (
                <div className="v3-inline-form">
                  <input placeholder="API Base URL" value={baseUrlInput} onChange={(e) => setBaseUrlInput(e.target.value)} />
                  <input type="password" placeholder="新的 API Key（留空则不改）" value={keyInput} onChange={(e) => setKeyInput(e.target.value)} />
                  <button className="primary-button" onClick={() => void saveProvider(provider)} disabled={busy === provider.provider}>保存</button>
                  <button className="ghost-button" onClick={() => setEditing('')}>取消</button>
                </div>
              ) : null}
              <div className="v3-entry-row">
                <button className="ghost-button" onClick={() => { setEditing(provider.provider); setBaseUrlInput(provider.base_url || ''); }}>编辑供应商</button>
                <button className="ghost-button" onClick={() => void testProvider(provider)} disabled={busy === `test:${provider.provider}`}>测试默认模型</button>
                <button className="ghost-button" onClick={() => void refreshProviderModels(provider.provider)} disabled={busy === `refresh:${provider.provider}`}>刷新模型</button>
                <button className="danger-button" onClick={() => void deleteProvider(provider)} disabled={busy === `delete:${provider.provider}`}>删除供应商</button>
              </div>
              <div className="v3-inline-form">
                <input placeholder="模型 ID" value={modelInput[provider.provider] || ''} onChange={(e) => setModelInput((prev) => ({ ...prev, [provider.provider]: e.target.value }))} />
                <button className="ghost-button" onClick={() => void addModel(provider.provider)}>添加模型</button>
              </div>
              {displayModels.length || provider.model ? (
                <div className="v3-model-chip-row">
                  {(displayModels.length ? displayModels : [{ id: `${provider.provider}:${provider.model}`, model_id: provider.model || '', display_name: null, source: 'configured', is_favorite: true }]).map((model) => (
                    <span key={model.id}>
                      {model.model_id}
                      <small>
                        {model.test_status === 'test_passed'
                          ? `可用${model.latency_ms ? ` ${model.latency_ms}ms` : ''}`
                          : model.test_status === 'test_failed'
                            ? `不可用${model.failure_reason ? `：${providerFailureText(model.failure_reason)}` : ''}`
                            : '未测试'}
                      </small>
                      <button onClick={() => void testModel(provider.provider, model.model_id)} disabled={busy === `test-model:${provider.provider}:${model.model_id}`}>测试此模型</button>
                      {model.model_id !== provider.model ? <button onClick={() => void setDefaultModel(provider, model.model_id)}>设为默认</button> : null}
                      <button disabled={model.model_id === provider.model && model.is_favorite} title={model.model_id === provider.model ? '默认模型不能停用' : ''} onClick={() => void providerCatalogApi.addCustomModel(provider.provider, { model_id: model.model_id, display_name: model.display_name || undefined, is_favorite: !model.is_favorite }).then(() => loadModels(provider.provider))}>{model.is_favorite ? '停用' : '启用'}</button>
                      {model.model_id !== provider.model ? <button onClick={() => void providerCatalogApi.deleteCustomModel(provider.provider, model.model_id).then(() => loadModels(provider.provider))}>删除</button> : null}
                    </span>
                  ))}
                </div>
              ) : null}
            </article>
          );
        }) : <EmptyState text="当前没有真实配置的模型供应商。" />}
      </div>
    </>
  );
}

export function V3RoutingSettingsPanel() {
  const roles = ['clarifier', 'planner', 'decomposer', 'executor', 'reviewer', 'tester'];
  const [options, setOptions] = useState<ProviderOption[]>([]);
  const [selected, setSelected] = useState<Record<string, ProviderOption>>({});
  const [preset, setPreset] = useState('development');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    void Promise.all([modelAssignmentApi.options(), modelAssignmentApi.get()]).then(([optionsData, assignmentData]) => {
      const raw = ((optionsData as Record<string, unknown>).options as ProviderOption[] || []);
      const record = assignmentData as Record<string, unknown>;
      const saved = (record.roles as Record<string, ProviderOption>) || {};
      const merged = raw.filter((option) => option.configured !== false && option.is_favorite !== false);
      Object.values(saved).forEach((current) => {
        if (current?.provider && current?.model && !merged.some((option) => option.provider === current.provider && option.model === current.model)) {
          merged.push(current);
        }
      });
      setOptions(merged);
      setPreset(String(record.preset || 'development'));
      setSelected(saved);
    });
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await modelAssignmentApi.save({ preset, roles: selected });
      setMessage('模型分工已保存。');
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className="v3-form-panel">
      <h2>模型分工</h2>
      {message ? <div className="v3-note"><p>{message}</p></div> : null}
      <label className="v3-compact-label">常用方案<select value={preset} onChange={(e) => setPreset(e.target.value)}><option value="development">开发默认</option><option value="live_demo">演示默认</option></select></label>
      <div className="v3-routing-grid">
        {roles.map((role) => {
          const current = selected[role];
          const value = current ? `${current.provider}/${current.model}` : '';
          return (
            <label key={role}>
              <span>{roleLabels[role]}</span>
              <select value={value} onChange={(e) => {
                const opt = options.find((item) => `${item.provider}/${item.model}` === e.target.value);
                if (opt) setSelected((prev) => ({ ...prev, [role]: opt }));
              }}>
                <option value="">请选择模型</option>
                {options.map((option) => <option key={`${role}-${option.provider}-${option.model}`} value={`${option.provider}/${option.model}`}>{providerDisplayName(option.provider)} · {option.model}</option>)}
              </select>
            </label>
          );
        })}
      </div>
      <button className="primary-button" onClick={() => void save()} disabled={saving}>{saving ? '保存中…' : '保存分工'}</button>
    </section>
  );
}

export function V3ConnectionsPanel({ resources }: { resources: V3Resources | null }) {
  const [profiles, setProfiles] = useState<AccessProfile[]>([]);
  const [tab, setTab] = useState<'vps' | 'github'>('vps');
  const [message, setMessage] = useState('');
  const [editingProfile, setEditingProfile] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({ port: '22', default_branch: 'main', auth_type: 'password', environment: 'staging' });
  const load = useCallback(async () => {
    const data = await accessProfilesApi.list();
    setProfiles((data.profiles || []) as AccessProfile[]);
  }, []);
  useEffect(() => { void load().catch((err) => setMessage(err instanceof Error ? err.message : String(err))); }, [load]);
  const check = async (profile: AccessProfile) => {
    const fn = profile.profile_type === 'vps' ? accessProfilesApi.checkVps : accessProfilesApi.checkGithub;
    const result = await fn(profile.id, { dry_run: false, socket_timeout: 5 });
    setMessage(`${profile.name}: ${valueText(result.status, 'unknown')}`);
    await load();
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (tab === 'vps') {
      const payload = {
        name: form.name,
        host: form.host,
        port: Number(form.port) || 22,
        username: form.username,
        auth_type: form.auth_type || 'password',
        password_value: form.password_value || undefined,
        ssh_key_value: form.ssh_key_value || undefined,
        default_workspace: form.default_workspace || undefined,
        environment: form.environment || 'staging',
      };
      if (editingProfile) await accessProfilesApi.updateVps(editingProfile, payload);
      else await accessProfilesApi.createVps(payload);
    } else {
      const payload = {
        name: form.name,
        owner: form.owner,
        repo: form.repo,
        default_branch: form.default_branch || 'main',
        token_value: form.token_value || undefined,
        token_ref: form.token_ref || undefined,
        allow_push_master: false,
      };
      if (editingProfile) await accessProfilesApi.updateGithub(editingProfile, payload);
      else await accessProfilesApi.createGithub(payload);
    }
    setMessage(`${tab === 'vps' ? 'VPS' : 'GitHub'} 配置已保存，密码和令牌不会回显。`);
    setForm({ port: '22', default_branch: 'main', auth_type: 'password', environment: 'staging' });
    setEditingProfile('');
    setShowEditor(false);
    await load();
  };
  const startCreate = () => {
    setEditingProfile('');
    setForm({ port: '22', default_branch: 'main', auth_type: 'password', environment: 'staging' });
    setShowEditor(true);
  };
  const startEdit = (profile: AccessProfile) => {
    setEditingProfile(profile.id);
    setForm({
      name: valueText(profile.name, ''),
      host: valueText(profile.host, ''),
      port: valueText(profile.port, '22'),
      username: valueText(profile.username, ''),
      auth_type: valueText(profile.auth_type, 'password'),
      default_workspace: valueText(profile.default_workspace, ''),
      environment: valueText(profile.environment, 'staging'),
      owner: valueText(profile.owner, ''),
      repo: valueText(profile.repo, ''),
      default_branch: valueText(profile.default_branch, 'main'),
      token_ref: valueText(profile.token_ref, ''),
    });
    setShowEditor(true);
  };
  const filtered = profiles
    .filter((profile) => profile.profile_type === tab)
    .filter((profile, idx, arr) => {
      const key = profile.profile_type === 'github'
        ? `${profile.owner || ''}/${profile.repo || ''}`.toLowerCase()
        : String(profile.name || profile.id).toLowerCase();
      return arr.findIndex((item) => {
        const other = item.profile_type === 'github'
          ? `${item.owner || ''}/${item.repo || ''}`.toLowerCase()
          : String(item.name || item.id).toLowerCase();
        return other === key;
      }) === idx;
    });
  const resourceItems = resources?.items || [];
  return (
    <>
      <div className="v3-kpis">{Object.entries(resources?.counts || {}).map(([k, v]) => <div className="v3-kpi" key={k}><small>{k === 'total' ? '资源总数' : k === 'vps' ? 'VPS' : k === 'github' ? 'GitHub' : k}</small><strong>{v}</strong></div>)}</div>
      <section className="v3-section wide">
        <header className="v3-section-head"><h2>资源连接总览</h2><span>GitHub、VPS、本地执行器和其他执行资源。</span></header>
        <div className="v3-resource-overview">
          {resourceItems.length ? resourceItems.map((item) => (
            <article key={item.resource_id}>
              <strong>{item.name}</strong>
              <span>{resourceTypeText(item.resource_type)}</span>
              <em>{statusText(item.connection_status, '未检查')} · 最近检查：{fmtTime(item.last_checked_at)}</em>
              {profiles.find((profile) => profile.profile_type === item.resource_type && profile.name === item.name)
                ? <button className="ghost-button" onClick={() => {
                  const profile = profiles.find((candidate) => candidate.profile_type === item.resource_type && candidate.name === item.name);
                  if (profile) void check(profile);
                }}>测试连接</button>
                : null}
            </article>
          )) : <EmptyState text="暂无资源连接记录。" />}
        </div>
      </section>
      <div className="v3-tabs compact">
        <button className={tab === 'vps' ? 'active' : ''} onClick={() => { setTab('vps'); setShowEditor(false); }}>VPS</button>
        <button className={tab === 'github' ? 'active' : ''} onClick={() => { setTab('github'); setShowEditor(false); }}>GitHub</button>
        <button className="ghost-button" onClick={startCreate}>添加{tab === 'vps' ? ' VPS' : ' GitHub'}资源</button>
      </div>
      {message ? <div className="v3-note"><p>{message}</p></div> : null}
      <div className={`v3-connection-layout${showEditor ? '' : ' overview-only'}`}>
        {showEditor ? <form className="v3-form-panel" onSubmit={submit}>
          <h2>{editingProfile ? '编辑' : '添加'}{tab === 'vps' ? ' VPS 资源' : ' GitHub 资源'}</h2>
          <label>名称<input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></label>
          {tab === 'vps' ? (
            <>
              <label>主机<input value={form.host || ''} onChange={(e) => setForm({ ...form, host: e.target.value })} required /></label>
              <label>端口<input value={form.port || '22'} onChange={(e) => setForm({ ...form, port: e.target.value })} /></label>
              <label>用户名<input value={form.username || ''} onChange={(e) => setForm({ ...form, username: e.target.value })} required /></label>
              <label>密码<input type="password" value={form.password_value || ''} onChange={(e) => setForm({ ...form, password_value: e.target.value })} /></label>
              <label>默认工作目录<input value={form.default_workspace || ''} onChange={(e) => setForm({ ...form, default_workspace: e.target.value })} /></label>
            </>
          ) : (
            <>
              <label>Owner<input value={form.owner || ''} onChange={(e) => setForm({ ...form, owner: e.target.value })} required /></label>
              <label>Repo<input value={form.repo || ''} onChange={(e) => setForm({ ...form, repo: e.target.value })} required /></label>
              <label>默认分支<input value={form.default_branch || 'main'} onChange={(e) => setForm({ ...form, default_branch: e.target.value })} /></label>
              <label>Token<input type="password" value={form.token_value || ''} onChange={(e) => setForm({ ...form, token_value: e.target.value })} /></label>
            </>
          )}
          <div className="v3-entry-row"><button className="primary-button">保存配置</button><button type="button" className="ghost-button" onClick={() => setShowEditor(false)}>取消</button></div>
        </form> : null}
        <section className="v3-section">
          <h2>{tab === 'vps' ? 'VPS 连接' : 'GitHub 连接'}</h2>
          {filtered.length ? filtered.map((profile) => (
            <article className="v3-resource-row" key={profile.id}>
              <strong>{profile.name}</strong>
              {profile.profile_type === 'github' ? (
                <dl className="v3-connection-facts">
                  <div><dt>账号/组织</dt><dd>{valueText(profile.owner, '-')}</dd></div>
                  <div><dt>仓库</dt><dd>{githubRepoName(profile)}</dd></div>
                  <div><dt>默认分支</dt><dd>{valueText(profile.default_branch, 'main')}</dd></div>
                </dl>
              ) : <p>{resourceSummary(profile)}</p>}
              <span>状态：{statusText(profile.last_status, '未检查')} · 最近检查：{fmtTime(profile.last_checked_at as string | null)}</span>
              {profile.profile_type === 'github' && githubFailureReason(profile) ? <p className="v3-blocking">{githubFailureReason(profile)}</p> : null}
              <div className="v3-entry-row">
                <button className="ghost-button" onClick={() => void check(profile)}>测试连接</button>
                <button className="ghost-button" onClick={() => startEdit(profile)}>{profile.profile_type === 'github' && githubFailureReason(profile) ? '重新连接 GitHub' : '编辑配置'}</button>
              </div>
            </article>
          )) : <EmptyState text="暂无资源配置。" />}
        </section>
      </div>
    </>
  );
}

export function V3SettingsPage({ tab }: { tab: 'models' | 'routing' | 'connections' | 'system' }) {
  const resourceLoad = useLoad<V3Resources>(() => v3Api.resources(), [tab === 'connections']);
  const systemLoad = useLoad<V3SystemDiagnostics>(() => v3Api.system(), [tab === 'system']);
  const tabs = [
    ['models', '模型配置'],
    ['routing', '模型分工'],
    ['connections', '资源与连接'],
    ['system', '系统诊断'],
  ] as const;
  return (
    <PageShell eyebrow="模型与系统" title="模型与系统" description="配置供应商、模型分工、VPS、GitHub 和系统诊断。">
      <nav className="v3-tabs">
        {tabs.map(([key, label]) => <Link key={key} className={tab === key ? 'active' : ''} to={`/v3/settings/${key === 'models' ? 'models' : key}`}>{label}</Link>)}
      </nav>
      {tab === 'models' ? (
        <V3ModelSettingsPanel />
      ) : null}
      {tab === 'routing' ? (
        <V3RoutingSettingsPanel />
      ) : null}
      {tab === 'connections' ? (
        resourceLoad.loading ? <LoadingState /> : resourceLoad.error ? <ErrorState message={resourceLoad.error} /> : <V3ConnectionsPanel resources={resourceLoad.data} />
      ) : null}
      {tab === 'system' ? (
        systemLoad.loading ? <LoadingState /> : systemLoad.error ? <ErrorState message={systemLoad.error} /> : (
          <>
            <DetailGrid rows={[
              ['Revision', systemLoad.data?.revision],
              ['Image Tag', systemLoad.data?.image_tag],
              ['运行环境', systemLoad.data?.environment],
              ['数据来源', systemLoad.data?.source],
            ]} />
            <div className="v3-kpis">{Object.entries(systemLoad.data?.counts || {}).map(([k, v]) => <div className="v3-kpi" key={k}><small>{k}</small><strong>{v}</strong></div>)}</div>
            <section className="v3-section wide">
              <h2>系统诊断</h2>
              {asList(systemLoad.data?.checks).map((check) => <p key={valueText(check.label)}>{valueText(check.label)} · {valueText(check.status)} · {valueText(check.detail)}</p>)}
            </section>
            <div className="v3-entry-row"><Link className="ghost-button" to="/settings">进入旧版系统设置</Link></div>
          </>
        )
      ) : null}
    </PageShell>
  );
}

export function V3PlaceholderPage({ kind }: { kind: string }) {
  const params = useParams();
  const title = useMemo(() => {
    const labels: Record<string, string> = {
      newRequirement: '创建新需求',
      takeover: '接手已有项目',
      importOutline: '导入已有大纲',
      requirementDetail: '需求详情',
      planOverview: '草稿概览',
      planOutline: '项目大纲',
      planTasks: '任务清单',
      planHistory: '计划历史',
      projectStages: '阶段和任务',
      projectFiles: '项目文件',
      projectHistory: '项目历史',
      deliveryCandidates: '候选交付',
      deliveryOfficial: '正式交付',
      deliveryIssues: '问题与返修',
    };
    return labels[kind] || 'V3 页面';
  }, [kind]);
  return (
    <PageShell
      eyebrow="V3 第一阶段"
      title={title}
      description="这一页的写操作暂未迁移。第一阶段只收口核心只读信息架构。"
    >
      <div className="v3-empty">
        <p>这里不会显示假按钮或假状态；需要操作时请先回旧版 UI。</p>
        {Object.keys(params).length ? <Diagnostics value={params} /> : null}
        <Link className="ghost-button" to="/">返回旧版 UI</Link>
      </div>
    </PageShell>
  );
}
